
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { SNAPSHOT_CHARTS, SNAPSHOT_WIDGETS, type SnapshotWidgetId } from '@/config/snapshot_cards';
import { 
  Search, 
  Package,
  Building,
  Ban, 
  X,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ArrowLeft,
  FileText,
  Building2,
  RefreshCw,
  Trophy,
  ExternalLink,
  Filter,
  Settings,
  Calendar,
  MessageSquare,
  Folder,
  Tag,
  Camera,
  GripVertical
} from 'lucide-react';
import { Role, ServiceCode, ImobiliariaStatus, ImobiliariaStatusBucket } from '@/types';
import { resolveImobiliariaStatus } from '@/utils/status';
import { WIDGET_CATALOG, WIDGET_SCOPE_PRODUTOS_GERAL } from '@/config/widgets';

// API base (mesma convenção da página Configurações)
const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const WIDGET_SCOPE = WIDGET_SCOPE_PRODUTOS_GERAL;
const SNAPSHOT_WIDGET_SCOPE = 'snapshot-cards';
type GeneralWidgetConfig = { key: string; label: string; requiredService?: string | null };
const GENERAL_WIDGETS: GeneralWidgetConfig[] = WIDGET_CATALOG
  .filter((widget) => widget.scope === WIDGET_SCOPE)
  .map((widget) => ({
    key: widget.key,
    label: widget.label,
    requiredService: widget.requiredService ?? null,
  }));

type CoordinationCacheData = {
  agencies: AssociatedAgency[];
  tickets: Atendimento[];
  ranking: TicketRankingItem[];
  categories: { id: number; nome: string; cor?: string | null }[];
  agenciesTotal: number;
  agenciesSummary: {
    analysis: number;
    credentialing: number;
    active: number;
    blocked: number;
    total: number;
  };
};

type PipelineCacheData = {
  snapshot: any | null;
  compare: any | null;
  snapshotDate: string | null;
  compareDate: string | null;
  delta: Record<string, { now: number; prev: number; diff: number; pct: number | null }> | null;
  series: Array<Record<string, any>>;
  error: string | null;
};

type AttendanceKpis = {
  range: { from: string; to: string };
  slaSeconds: number;
  firstResponse: { avgSeconds: number; withinSlaPct: number | null; withinSlaCount: number; respondedCount: number };
  resolution: { avgSeconds: number };
  backlog: { total: number };
  closed: { total: number };
  discard: { total: number; rate: number | null };
};

const WIDGET_CACHE_TTL_MS = 5 * 60 * 1000;

interface ProductsPageProps {
  darkMode: boolean;
  role: Role;
  // onNavigate: (page: PageId) => void;
  onNavigateToAgency?: (agencyId: number) => void;
  initialProductId?: number | null;
  onNavigateToProduct?: (id: number) => void;
  onExitProduct?: () => void;
  // onOpenPublicWizard?: (productId: number) => void;
}

const getAuthHeaders = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const rawToken = localStorage.getItem('va_token') || '';
  return rawToken ? { Authorization: rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}` } : {};
};

const toDateOnlyString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateOnly = (value?: string | null): Date | null => {
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length !== 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const parseProductRoute = () => {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/\/produtos\/(\d+)(?:\/([^\/]+))?(?:\/([^\/]+))?/);
  if (!match) return null;
  const productId = Number(match[1]) || null;
  const section = (match[2] || '').toLowerCase();
  const sub = (match[3] || '').toLowerCase();
  return { productId, section, sub };
};

const buildProductRoute = (productId: number, section?: string | null, sub?: string | null) => {
  if (typeof window === 'undefined') return `/produtos/${productId}`;
  const path = window.location.pathname;
  const idx = path.indexOf('/produtos/');
  const prefix = idx >= 0 ? path.slice(0, idx) : '';
  let next = `${prefix}/produtos/${productId}`;
  if (section) next += `/${section}`;
  if (sub) next += `/${sub}`;
  if (!next.startsWith('/')) next = `/${next}`;
  return next.replace(/\/{2,}/g, '/');
};

const diffDays = (from?: string | null, to?: string | null): number => {
  const fromDate = parseDateOnly(from || '');
  const toDate = parseDateOnly(to || '');
  if (!fromDate || !toDate) return 7;
  const delta = Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
  return Math.max(1, delta);
};

const getLastNDaysRange = (days: number) => {
  const today = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const to = toDateOnlyString(today);
  const from = toDateOnlyString(new Date(today.getTime() - (days - 1) * dayMs));
  return { from, to };
};

type WidgetDatePreset = 'one_day' | 'week' | 'month' | 'custom';

const getPresetRange = (preset: WidgetDatePreset) => {
  const today = new Date();
  const end = toDateOnlyString(today);
  const dayMs = 24 * 60 * 60 * 1000;

  if (preset === 'one_day') {
    const from = toDateOnlyString(new Date(today.getTime() - dayMs));
    return { from, to: end };
  }
  if (preset === 'week') {
    const offset = (today.getDay() + 6) % 7;
    const from = toDateOnlyString(new Date(today.getTime() - offset * dayMs));
    return { from, to: end };
  }
  if (preset === 'month') {
    const from = toDateOnlyString(new Date(today.getFullYear(), today.getMonth(), 1));
    return { from, to: end };
  }
  return { from: end, to: end };
};

// Interface for List View
interface Product {
  id: number;
  name: string;
  builder: string;
  status: 'active' | 'blocked';
  phase?: string;
  praca?: string | null;
  pracaNome?: string | null;
}

// Produto retornado pelo Core API
interface ProductApi {
  id: number;
  nome: string;
  cidade?: string;
  estado?: string;
  fase?: 'PRE_LANCAMENTO' | 'LANCAMENTO' | 'EM_VENDAS' | 'FINALIZADO';
  ativo: boolean;
  praca_id?: number | null;
  praca_nome?: string | null;
}

// Interfaces for Detail View
interface SalesVelocity {
  month: string;
  value: number;
}

interface ProductDashboard {
  sold_units: number;
  reserved_units: number;
  available_units: number;
  percent_sold: number;
  total_vgv: string;
  sold_vgv: string;
  construction_progress: number;
  financial_progress: number;
  sales_velocity: SalesVelocity[];
}

interface ProductSpecs {
  type: 'Vertical' | 'Horizontal' | 'Comercial';
  total_lots: number;
  min_sqm: number;
  max_sqm: number;
}

interface Interaction {
  id: string; // Ticket ID e.g. CH-1092
  date: string;
  broker: string;
  subject: string;
  status: string;
}

interface Atendimento {
  id: number;
  status: string;
  contato_nome?: string | null;
  contato_whatsapp: string;
  created_at?: string;
  updated_at?: string;
  atendente_nome?: string | null;
  categoria_id?: number | null;
  categoria_nome?: string | null;
}

interface AtendimentoMensagem {
  id: number;
  direction: 'IN' | 'OUT' | 'SYS';
  body: string;
  media_type?: string;
  media_url?: string | null;
  sender_name?: string | null;
  sent_at?: string | null;
}

interface ProductProfile extends Product {
  location: string;
  description: string;
  specs: ProductSpecs;
  dashboard: ProductDashboard;
  interactions?: Interaction[];
  registrationEnabled?: boolean;
}

interface AssociatedAgency {
  id: number;
  name: string;
  sold: number;
  vgv: string;
  status: ImobiliariaStatus;
  city?: string | null;
  state?: string | null;
  tags?: string[];
}

type PesquisaNode = {
  name: string;
  updated?: string;
  size?: string;
  type?: 'file';
  items?: PesquisaNode[];
};

type FilterStatus = ImobiliariaStatusBucket;
type TicketRankingItem = { categoryId: number | null; category: string; count: number };
type AgencySortColumn = 'name' | 'sold' | 'vgv' | 'status';
type AgencySortDirection = 'asc' | 'desc';
type CoordinationSummary = { analysis: number; credentialing: number; active: number; blocked: number; total: number };
type TagOption = { id: number; name: string };

const EMPTY_COORDINATION_SUMMARY: CoordinationSummary = {
  analysis: 0,
  credentialing: 0,
  active: 0,
  blocked: 0,
  total: 0,
};

const FILTER_STATUS_LABELS: Record<FilterStatus, string> = {
  analysis: 'Análise',
  credentialing: 'Credenciando',
  active: 'Ativas',
  blocked: 'Suspensas',
};

const IMOBILIARIA_STATUS_OPTIONS = [
  { value: 'EM_ANALISE', label: 'Em analise' },
  { value: 'CREDENCIANDO', label: 'Credenciando' },
  { value: 'APROVADA', label: 'Aprovada' },
  { value: 'REJEITADA', label: 'Rejeitada' },
  { value: 'SUSPENSA', label: 'Suspensa' },
];

const AGENCY_STATUS_EXPORT_LABELS: Record<FilterStatus, string> = {
  analysis: 'Em analise',
  credentialing: 'Credenciando',
  active: 'Aprovada',
  blocked: 'Suspensa',
};

export const ProductsPage: React.FC<ProductsPageProps> = ({ darkMode, role, onNavigateToAgency, initialProductId, onNavigateToProduct, onExitProduct }) => {
  const [viewMode, setViewMode] = useState<'list' | 'detail'>(() => {
    if (initialProductId) return 'detail';
    if (typeof window !== 'undefined' && new RegExp('^/produtos/\\\\d+').test(window.location.pathname)) return 'detail';
    return 'list';
  });
  // Data States
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductProfile | null>(null);
  const [associatedAgencies, setAssociatedAgencies] = useState<AssociatedAgency[]>([]);
  const [tickets, setTickets] = useState<Atendimento[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [ticketError, setTicketError] = useState('');
  const [ticketRanking, setTicketRanking] = useState<TicketRankingItem[]>([]);
  const [ticketRankingLoading, setTicketRankingLoading] = useState(false);
  const [ticketRankingError, setTicketRankingError] = useState<string | null>(null);
  const [categories, setCategories] = useState<{ id: number; nome: string; cor?: string | null }[]>([]);
  const [snapshotAgencies, setSnapshotAgencies] = useState<AssociatedAgency[]>([]);
  const [snapshotTickets, setSnapshotTickets] = useState<Atendimento[]>([]);
  const [snapshotTicketRanking, setSnapshotTicketRanking] = useState<TicketRankingItem[]>([]);
  const [snapshotCategories, setSnapshotCategories] = useState<{ id: number; nome: string; cor?: string | null }[]>([]);
  const [changeCategoryModalOpen, setChangeCategoryModalOpen] = useState(false);
  const [changeCategoryValue, setChangeCategoryValue] = useState<string>('');
  const [changeCategoryLoading, setChangeCategoryLoading] = useState(false);
  const [changeCategoryDirty, setChangeCategoryDirty] = useState(false);
  const [coordinationRange, setCoordinationRange] = useState<'4w' | '8w'>('4w');
  const [selectedTicket, setSelectedTicket] = useState<Atendimento | null>(null);
  const [ticketMessages, setTicketMessages] = useState<AtendimentoMensagem[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [serviceTab, setServiceTab] = useState<'GERAL' | 'Coordenacao' | 'GESTAO' | 'SDR' | 'PESQUISA'>('Coordenacao');
  const [coordinationTab, setCoordinationTab] = useState<'credenciamento' | 'atendimentos'>('credenciamento');
  const [activeServices, setActiveServices] = useState<ServiceCode[]>([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [widgetSettingsOpen, setWidgetSettingsOpen] = useState(false);
  const [snapshotCardPrefs, setSnapshotCardPrefs] = useState<Record<string, boolean>>({});
  const [snapshotCardsLoading, setSnapshotCardsLoading] = useState(false);
  const [snapshotCardsSaving, setSnapshotCardsSaving] = useState(false);
  const [snapshotConfigOpen, setSnapshotConfigOpen] = useState(false);
  const [snapshotConfigLoading, setSnapshotConfigLoading] = useState(false);
  const [snapshotConfigSaving, setSnapshotConfigSaving] = useState(false);
  const [snapshotConfigError, setSnapshotConfigError] = useState<string | null>(null);
  const [snapshotConfigDraft, setSnapshotConfigDraft] = useState<{ enabled: boolean; hour: number; minute: number; windowDays: number } | null>(null);
  const [manualSnapshotOpen, setManualSnapshotOpen] = useState(false);
  const [manualSnapshotDate, setManualSnapshotDate] = useState<string>(() => toDateOnlyString(new Date()));
  const [manualSnapshotLoading, setManualSnapshotLoading] = useState(false);
  const [manualSnapshotError, setManualSnapshotError] = useState<string | null>(null);
  const [snapshotWidgetOrder, setSnapshotWidgetOrder] = useState<SnapshotWidgetId[]>(() => SNAPSHOT_WIDGETS.map((card) => card.id));
  const [draggingWidgetId, setDraggingWidgetId] = useState<SnapshotWidgetId | null>(null);
  const [dragOverWidgetId, setDragOverWidgetId] = useState<SnapshotWidgetId | null>(null);
  const [snapshotOrderDirty, setSnapshotOrderDirty] = useState(false);
  const [draggingSnapshotWidget, setDraggingSnapshotWidget] = useState(false);
  const [widgetDatePreset, setWidgetDatePreset] = useState<WidgetDatePreset>('custom');
  const [widgetDateFrom, setWidgetDateFrom] = useState<string>(() => getLastNDaysRange(7).from);
  const [widgetDateTo, setWidgetDateTo] = useState<string>(() => getLastNDaysRange(7).to);
  const [draftDatePreset, setDraftDatePreset] = useState<WidgetDatePreset>('custom');
  const [draftDateFrom, setDraftDateFrom] = useState<string>(() => getLastNDaysRange(7).from);
  const [draftDateTo, setDraftDateTo] = useState<string>(() => getLastNDaysRange(7).to);
  const [dateControlsOpen, setDateControlsOpen] = useState(false);
  const [registrationByProduct, setRegistrationByProduct] = useState<Record<number, boolean>>({});
  const [ticketsPage, setTicketsPage] = useState(1);
  const [agenciesPage, setAgenciesPage] = useState(1);
  const [agenciesTotal, setAgenciesTotal] = useState(0);
  const [coordinationStatusSummary, setCoordinationStatusSummary] = useState({
    analysis: 0,
    credentialing: 0,
    active: 0,
    blocked: 0,
    total: 0,
  });
  const [coordinationLiveSummary, setCoordinationLiveSummary] = useState<CoordinationSummary>(EMPTY_COORDINATION_SUMMARY);
  const [coordinationLiveUpdatedAt, setCoordinationLiveUpdatedAt] = useState<Date | null>(null);
  const [snapshotAgenciesTotal, setSnapshotAgenciesTotal] = useState(0);
  const [snapshotCoordinationSummary, setSnapshotCoordinationSummary] = useState({
    analysis: 0,
    credentialing: 0,
    active: 0,
    blocked: 0,
    total: 0,
  });
  const [debouncedAgencySearch, setDebouncedAgencySearch] = useState('');
  const [ticketCategoryFilter, setTicketCategoryFilter] = useState<{ id: number | null; label: string } | null>(null);
  const [coordinationLastUpdated, setCoordinationLastUpdated] = useState<Date | null>(null);
  const [snapshotCoordinationLastUpdated, setSnapshotCoordinationLastUpdated] = useState<Date | null>(null);
  const [isRefreshingCoordination, setIsRefreshingCoordination] = useState(false);
  const [pipelineSnapshot, setPipelineSnapshot] = useState<any | null>(null);
  const [pipelineCompare, setPipelineCompare] = useState<any | null>(null);
  const [pipelineSnapshotDate, setPipelineSnapshotDate] = useState<string | null>(null);
  const [pipelineCompareDate, setPipelineCompareDate] = useState<string | null>(null);
  const [pipelineDelta, setPipelineDelta] = useState<Record<string, { now: number; prev: number; diff: number; pct: number | null }> | null>(null);
  const [pipelineSeries, setPipelineSeries] = useState<Array<Record<string, any>>>([]);
  const [pipelineSnapshotLoading, setPipelineSnapshotLoading] = useState(false);
  const [pipelineSnapshotError, setPipelineSnapshotError] = useState<string | null>(null);
  const [engagementSnapshot, setEngagementSnapshot] = useState<{
    snapshotDate: string | null;
    range: { from: string | null; to: string | null };
    engajamento: {
      imobiliariasTotal: number;
      chamadosTotal: number;
      mediaChamadosPorImobiliaria: number;
      categoriaMaisFrequente: { id: number | null; nome: string | null; total: number };
    };
  } | null>(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [engagementError, setEngagementError] = useState<string | null>(null);
  const [attendanceKpis, setAttendanceKpis] = useState<AttendanceKpis | null>(null);
  const [attendanceKpisLoading, setAttendanceKpisLoading] = useState(false);
  const [attendanceKpisError, setAttendanceKpisError] = useState<string | null>(null);
  const coordinationCacheRef = React.useRef(new Map<string, { ts: number; data: CoordinationCacheData }>());
  const coordinationSummaryCacheRef = React.useRef(new Map<string, { ts: number; summary: CoordinationSummary }>());
  const coordinationSummaryRef = React.useRef(coordinationStatusSummary);
  const pipelineCacheRef = React.useRef(new Map<string, { ts: number; data: PipelineCacheData }>());
  const lastRouteRef = useRef<string | null>(null);
  const manualTabChangeRef = useRef(false);
  const dateFromInputRef = useRef<HTMLInputElement | null>(null);
  const dateToInputRef = useRef<HTMLInputElement | null>(null);
  
  // UI States
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  
  // Filtering within Detail View (Associated Agencies)
  const [agencySearch, setAgencySearch] = useState('');
  const [statusFilters, setStatusFilters] = useState<FilterStatus[]>([]);
  const [availableAgencyTags, setAvailableAgencyTags] = useState<TagOption[]>([]);
  const [selectedAgencyTags, setSelectedAgencyTags] = useState<string[]>([]);
  const [agencyTagsMode, setAgencyTagsMode] = useState<'any' | 'all'>('any');
  const [agencySort, setAgencySort] = useState<{ column: AgencySortColumn; direction: AgencySortDirection }>({
    column: 'name',
    direction: 'asc',
  });
  const [agenciesPageSize, setAgenciesPageSize] = useState<number | 'all'>(5);
  const [customAgenciesPageSize, setCustomAgenciesPageSize] = useState('5');
  const [customAgenciesPageSizeActive, setCustomAgenciesPageSizeActive] = useState(false);
  const [bulkSelectionEnabled, setBulkSelectionEnabled] = useState(false);
  const [selectedAgencyIds, setSelectedAgencyIds] = useState<number[]>([]);
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Specs Editing State
  const [tempPhase, setTempPhase] = useState<string>('LANCAMENTO');
  const [tempSpecs, setTempSpecs] = useState<ProductSpecs | null>(null);
  const skipAutoOpenRef = React.useRef(false);
  const autoOpenRef = React.useRef<number | null>(null);
  const isStatusSelected = useCallback(
    (status: FilterStatus) => statusFilters.includes(status),
    [statusFilters]
  );
  const toggleAgencyTag = useCallback((name: string) => {
    setSelectedAgencyTags((current) => {
      if (current.includes(name)) {
        return current.filter((tag) => tag !== name);
      }
      return [...current, name];
    });
  }, []);
  const clearAgencyTags = useCallback(() => {
    setSelectedAgencyTags([]);
  }, []);
  const handleAgencySort = useCallback((column: AgencySortColumn) => {
    setAgencySort((current) => {
      if (current.column === column) {
        return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      const direction: AgencySortDirection = column === 'name' || column === 'status' ? 'asc' : 'desc';
      return { column, direction };
    });
  }, [widgetDateTo]);
  const getAgencySortIndicator = useCallback(
    (column: AgencySortColumn) => {
      if (agencySort.column !== column) return '';
      return agencySort.direction === 'asc' ? ' ^' : ' v';
    },
    [agencySort]
  );
  const isAdmin = useMemo(() => (role || '').toLowerCase().includes('admin'), [role]);
  useEffect(() => {
    if (!bulkSelectionEnabled) {
      setSelectedAgencyIds([]);
      setBulkActionOpen(false);
      setBulkStatus('');
    }
  }, [bulkSelectionEnabled]);
  useEffect(() => {
    if (statusFilters.length === 0) {
      setSelectedAgencyTags([]);
      setAgencyTagsMode('any');
    }
  }, [statusFilters.length]);
  useEffect(() => {
    if (!isAdmin && bulkSelectionEnabled) {
      setBulkSelectionEnabled(false);
    }
  }, [isAdmin, bulkSelectionEnabled]);
  const toggleAgencySelection = useCallback((agencyId: number) => {
    setSelectedAgencyIds((current) => {
      if (current.includes(agencyId)) {
        return current.filter((id) => id !== agencyId);
      }
      return [...current, agencyId];
    });
  }, []);
  const coordinationAgencies = serviceTab === 'GERAL' ? snapshotAgencies : associatedAgencies;
  const coordinationTickets = serviceTab === 'GERAL' ? snapshotTickets : tickets;
  const coordinationRanking = serviceTab === 'GERAL' ? snapshotTicketRanking : ticketRanking;
  const coordinationCategories = serviceTab === 'GERAL' ? snapshotCategories : categories;
  const coordinationSummary = serviceTab === 'GERAL' ? coordinationLiveSummary : coordinationStatusSummary;
  const coordinationUpdatedAt = serviceTab === 'GERAL' ? coordinationLiveUpdatedAt : coordinationLastUpdated;
  const isWidgetAvailable = useCallback(
    (key: string) => {
      const widget = GENERAL_WIDGETS.find((item) => item.key === key);
      if (!widget) return false;
      if (widget.requiredService && !activeServices.includes(widget.requiredService as ServiceCode)) {
        return false;
      }
      return true;
    },
    [activeServices]
  );
  const isSnapshotCardEnabled = useCallback(
    (id: string) => {
      const stored = snapshotCardPrefs[id];
      if (stored !== undefined) return stored;
      const card = SNAPSHOT_WIDGETS.find((item) => item.id === id);
      return card ? card.active : true;
    },
    [snapshotCardPrefs]
  );
  const orderedSnapshotWidgets = useMemo(() => {
    const byId = new Map(SNAPSHOT_WIDGETS.map((card) => [card.id, card]));
    const ordered = snapshotWidgetOrder.map((id) => byId.get(id)).filter(Boolean) as typeof SNAPSHOT_WIDGETS;
    const missing = SNAPSHOT_WIDGETS.filter((card) => !ordered.some((item) => item.id === card.id));
    return [...ordered, ...missing];
  }, [snapshotWidgetOrder]);
  const moveSnapshotWidget = useCallback((fromId: SnapshotWidgetId, toId: SnapshotWidgetId) => {
    if (fromId === toId) return;
    setSnapshotWidgetOrder((current) => {
      const next = [...current];
      const fromIndex = next.indexOf(fromId);
      const toIndex = next.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) return current;
      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, fromId);
      return next;
    });
    setSnapshotOrderDirty(true);
  }, []);
  const persistSnapshotCardPrefs = useCallback(
    async (prefs: Record<string, boolean>, productId?: number | null) => {
      setSnapshotCardsSaving(true);
      try {
        const cards = orderedSnapshotWidgets.length ? orderedSnapshotWidgets : SNAPSHOT_WIDGETS;
        const payload = {
          scope: SNAPSHOT_WIDGET_SCOPE,
          produtoId: productId,
          widgets: cards.map((card, index) => ({
            key: card.id,
            enabled: prefs[card.id] !== undefined ? prefs[card.id] : card.active,
            position: index + 1,
            requiredService: null,
          })),
        };
        const response = await fetch(`${CORE_API}/widgets`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Falha ao salvar cards de snapshot');
      } catch (err) {
        console.error('Erro ao salvar configuracoes de cards de snapshot', err);
      } finally {
        setSnapshotCardsSaving(false);
      }
    },
    [orderedSnapshotWidgets]
  );
  const loadSnapshotCardPrefs = useCallback(
    async (productId?: number | null) => {
      setSnapshotCardsLoading(true);
      try {
        const params = new URLSearchParams({ scope: SNAPSHOT_WIDGET_SCOPE });
        if (productId) {
          params.set('produtoId', String(productId));
        }
        const response = await fetch(`${CORE_API}/widgets?${params.toString()}`, { headers: { ...getAuthHeaders() } });
        if (!response.ok) throw new Error('Falha ao carregar cards de snapshot');
        const data = await response.json();
        const widgets = Array.isArray(data?.widgets) ? data.widgets : [];
        const prefs: Record<string, boolean> = {};
        widgets.forEach((widget: any) => {
          if (!widget?.key) return;
          prefs[String(widget.key)] = widget.enabled !== undefined ? !!widget.enabled : true;
        });
        setSnapshotCardPrefs(prefs);
        const orderedKeys = widgets
          .filter((widget: any) => widget?.key && widget?.position !== undefined)
          .sort((a: any, b: any) => Number(a.position) - Number(b.position))
          .map((widget: any) => String(widget.key) as SnapshotWidgetId);
        if (orderedKeys.length) {
          const known = new Set(SNAPSHOT_WIDGETS.map((card) => card.id));
          setSnapshotWidgetOrder(orderedKeys.filter((key: SnapshotWidgetId) => known.has(key)));
        }
      } catch (err) {
        console.error('Erro ao carregar configuracoes de cards de snapshot', err);
      } finally {
        setSnapshotCardsLoading(false);
      }
    },
    []
  );
  const loadSnapshotConfig = useCallback(async (productId?: number | null) => {
    if (!productId) return;
    setSnapshotConfigLoading(true);
    setSnapshotConfigError(null);
    try {
      const response = await fetch(`${CORE_API}/produtos/${productId}/snapshot-config`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar configuracao de snapshots');
      const data = await response.json();
      const config = data?.config || {};
      setSnapshotConfigDraft({
        enabled: config.enabled !== undefined ? !!config.enabled : true,
        hour: Number(config.hour ?? 2),
        minute: Number(config.minute ?? 0),
        windowDays: Number(config.windowDays ?? 7),
      });
    } catch (err) {
      console.error('Erro ao carregar configuracao de snapshots', err);
      setSnapshotConfigError('Falha ao carregar configuracao de snapshots.');
    } finally {
      setSnapshotConfigLoading(false);
    }
  }, []);

  const saveSnapshotConfig = useCallback(async () => {
    if (!selectedProduct?.id || !snapshotConfigDraft) return;
    setSnapshotConfigSaving(true);
    setSnapshotConfigError(null);
    try {
      const payload = {
        enabled: snapshotConfigDraft.enabled,
        hour: snapshotConfigDraft.hour,
        minute: snapshotConfigDraft.minute,
        windowDays: snapshotConfigDraft.windowDays,
      };
      const response = await fetch(`${CORE_API}/produtos/${selectedProduct.id}/snapshot-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Falha ao salvar configuracao de snapshots');
      setSnapshotConfigOpen(false);
    } catch (err) {
      console.error('Erro ao salvar configuracao de snapshots', err);
      setSnapshotConfigError('Falha ao salvar configuracao de snapshots.');
    } finally {
      setSnapshotConfigSaving(false);
    }
  }, [selectedProduct?.id, snapshotConfigDraft]);
  const handleToggleSnapshotCard = useCallback(
    (id: string) => {
      setSnapshotCardPrefs((prev) => {
        const nextValue = !(prev[id] ?? true);
        const next = { ...prev, [id]: nextValue };
        persistSnapshotCardPrefs(next, selectedProduct?.id);
        return next;
      });
    },
    [persistSnapshotCardPrefs, selectedProduct?.id]
  );

  useEffect(() => {
    if (!snapshotOrderDirty) return;
    persistSnapshotCardPrefs(snapshotCardPrefs, selectedProduct?.id);
    setSnapshotOrderDirty(false);
  }, [snapshotOrderDirty, snapshotCardPrefs, selectedProduct?.id, persistSnapshotCardPrefs]);

  const generalWidgetOrder = useMemo(() => {
    const order: string[] = [];
    orderedSnapshotWidgets.forEach((widget) => {
      if (!isSnapshotCardEnabled(widget.id)) return;
      widget.widgetKeys.forEach((key) => {
        order.push(key);
      });
    });
    return order;
  }, [orderedSnapshotWidgets, isSnapshotCardEnabled]);

  const datePresetOptions = useMemo(() => ([
    { value: 'one_day', label: '1DIA' },
    { value: 'week', label: 'SEMANA' },
    { value: 'month', label: 'MÊS' },
    { value: 'custom', label: 'PERSONALIZADO' },
  ]), []);

  const currentPresetLabel = useMemo(() => {
    const match = datePresetOptions.find((option) => option.value === widgetDatePreset);
    return match?.label || 'Personalizado';
  }, [datePresetOptions, widgetDatePreset]);

  const handlePresetChange = (value: WidgetDatePreset) => {
    setDraftDatePreset(value);
    if (value === 'custom') return;
    const range = getPresetRange(value);
    setDraftDateFrom(range.from);
    setDraftDateTo(range.to);
  };

  const handleDateFromChange = (value: string) => {
    setDraftDatePreset('custom');
    setDraftDateFrom(value);
    setDraftDateTo(value);
  };

  const handleDateToChange = (value: string) => {
    setDraftDatePreset('custom');
    setDraftDateTo(value);
    if (draftDateFrom && value < draftDateFrom) {
      setDraftDateFrom(value);
    }
  };

  const applyWidgetDateRange = () => {
    setWidgetDatePreset(draftDatePreset);
    setWidgetDateFrom(draftDateFrom);
    setWidgetDateTo(draftDateTo);
    setDateControlsOpen(false);
  };

  const openDateControls = () => {
    setDraftDatePreset(widgetDatePreset);
    setDraftDateFrom(widgetDateFrom);
    setDraftDateTo(widgetDateTo);
    setDateControlsOpen(true);
  };

  const toggleWidgetSettings = () => {
    setWidgetSettingsOpen((prev) => {
      const next = !prev;
      if (next) {
        setDateControlsOpen(false);
      }
      return next;
    });
  };

  const openSnapshotConfig = () => {
    setSnapshotConfigOpen(true);
    loadSnapshotConfig(selectedProduct?.id);
  };

  const closeSnapshotConfig = () => {
    setSnapshotConfigOpen(false);
    setSnapshotConfigError(null);
  };

  const openManualSnapshot = () => {
    const baseDate = widgetDateTo || toDateOnlyString(new Date());
    setManualSnapshotDate(baseDate);
    setManualSnapshotError(null);
    setManualSnapshotOpen(true);
  };

  const closeManualSnapshot = () => {
    setManualSnapshotOpen(false);
    setManualSnapshotError(null);
  };


  const renderWidgetDateControls = () => {
    if (!dateControlsOpen) return null;

    const openDatePicker = (ref: React.MutableRefObject<HTMLInputElement | null>) => {
      const input = ref.current;
      if (!input || input.disabled) return;
      if (typeof (input as any).showPicker === 'function') {
        (input as any).showPicker();
      } else {
        input.focus();
      }
    };

    const maxSnapshotDate = toDateOnlyString(new Date());

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className={`w-full max-w-lg rounded-2xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <div className="text-center flex-1">
              <p className="text-sm font-semibold">Periodo do snapshot</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Defina o intervalo para atualizar os widgets da aba Snapshot.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDateControlsOpen(false)}
              className={`p-2 rounded-lg border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex flex-wrap justify-center gap-2">
              {[
                { value: 'one_day', label: '1DIA' },
                { value: 'week', label: 'SEMANA' },
                { value: 'month', label: 'MÊS' },
                { value: 'custom', label: 'PERSONALIZADO' },
              ].map((preset) => {
                const isActive = draftDatePreset === preset.value;
                return (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => handlePresetChange(preset.value as WidgetDatePreset)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      isActive
                        ? darkMode
                          ? 'bg-vision-activeRed border-vision-activeRed text-white'
                          : 'bg-[#294b29] border-[#294b29] text-white'
                        : darkMode
                        ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-end justify-center gap-3">
              <div className="flex flex-col gap-1">
                <label className={`text-[11px] font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>De</label>
                <input
                  ref={dateFromInputRef}
                  type="date"
                  value={draftDateFrom}
                  max={maxSnapshotDate}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                  onClick={() => openDatePicker(dateFromInputRef)}
                  disabled={draftDatePreset !== 'custom'}
                  className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-800'} ${draftDatePreset !== 'custom' ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className={`text-[11px] font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Ate</label>
                <input
                  ref={dateToInputRef}
                  type="date"
                  value={draftDateTo}
                  max={maxSnapshotDate}
                  onChange={(e) => handleDateToChange(e.target.value)}
                  onClick={() => openDatePicker(dateToInputRef)}
                  disabled={draftDatePreset !== 'custom'}
                  className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-800'} ${draftDatePreset !== 'custom' ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
              </div>
            </div>
          </div>
          <div className={`flex items-center justify-end gap-2 p-4 border-t ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={() => setDateControlsOpen(false)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={applyWidgetDateRange}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'bg-vision-activeRed border-vision-activeRed text-white hover:bg-vision-activeRed/90'
                  : 'bg-[#294b29] border-[#294b29] text-white hover:bg-[#1a301a]'
              }`}
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    );
  };
  const renderWidgetSettingsModal = () => {
    if (serviceTab !== 'GERAL' || !widgetSettingsOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className={`w-full max-w-2xl rounded-2xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className={`flex items-center justify-between gap-4 p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <div>
              <p className="text-sm font-semibold">Widgets do dashboard</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Ative ou desative os cards de snapshot do dashboard.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className={`text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {snapshotCardsSaving ? 'Salvando...' : snapshotCardsLoading ? 'Carregando...' : ''}
              </div>
              <button
                type="button"
                onClick={() => setWidgetSettingsOpen(false)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                  darkMode
                    ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
                aria-label="Fechar widgets"
              >
                <X size={14} />
              </button>
            </div>
          </div>
          <div className="p-4">
            <div className="flex flex-col gap-3">
              {orderedSnapshotWidgets.map((card) => {
                const enabled = isSnapshotCardEnabled(card.id);
                const isDragging = draggingWidgetId === card.id;
                const isDragOver = dragOverWidgetId === card.id && draggingWidgetId !== card.id;
                return (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => {
                      if (!draggingSnapshotWidget) {
                        handleToggleSnapshotCard(card.id);
                      }
                    }}
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = 'move';
                      setDraggingWidgetId(card.id);
                      setDraggingSnapshotWidget(true);
                    }}
                    onDragEnd={() => {
                      setDraggingWidgetId(null);
                      setDragOverWidgetId(null);
                      setDraggingSnapshotWidget(false);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      if (dragOverWidgetId !== card.id) {
                        setDragOverWidgetId(card.id);
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverWidgetId === card.id) {
                        setDragOverWidgetId(null);
                      }
                    }}
                    onDrop={() => {
                      if (draggingWidgetId) {
                        moveSnapshotWidget(draggingWidgetId, card.id);
                      }
                      setDragOverWidgetId(null);
                    }}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      enabled
                        ? darkMode
                          ? 'bg-vision-surface border-vision-border text-gray-100'
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                        : darkMode
                        ? 'bg-transparent border-vision-border text-gray-400'
                        : 'bg-white border-gray-200 text-gray-500'
                    } ${isDragging ? 'opacity-70' : ''} ${isDragOver ? (darkMode ? 'ring-2 ring-vision-activeRed/60' : 'ring-2 ring-[#294b29]/40') : ''}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} cursor-grab`}>
                        <GripVertical size={16} />
                      </span>
                      <span className="flex flex-col items-start">
                        <span>{card.title}</span>
                        {card.subtitle ? (
                          <span className={`text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{card.subtitle}</span>
                        ) : null}
                      </span>
                    </span>
                    <span className={`text-xs ${enabled ? (darkMode ? 'text-green-300' : 'text-green-700') : (darkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                      {enabled ? 'Ativo' : 'Oculto'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSnapshotConfigModal = () => {
    if (serviceTab !== 'GERAL' || !snapshotConfigOpen) return null;
    const timeValue = snapshotConfigDraft
      ? `${String(snapshotConfigDraft.hour).padStart(2, '0')}:${String(snapshotConfigDraft.minute).padStart(2, '0')}`
      : '02:00';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className={`w-full max-w-lg rounded-2xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className={`flex items-center justify-between gap-4 p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <div>
              <p className="text-sm font-semibold">Agendamento de snapshots</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Configure quando gerar os snapshots do produto.
              </p>
            </div>
            <button
              type="button"
              onClick={closeSnapshotConfig}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Fechar configuracao de snapshots"
            >
              <X size={14} />
            </button>
          </div>
          <div className="p-4 space-y-4">
            {snapshotConfigError ? (
              <p className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                {snapshotConfigError}
              </p>
            ) : null}
            <label className="flex items-center justify-between text-sm">
              <span>Snapshots ativos</span>
              <input
                type="checkbox"
                checked={snapshotConfigDraft?.enabled ?? true}
                onChange={(e) => setSnapshotConfigDraft((prev) => prev ? { ...prev, enabled: e.target.checked } : prev)}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold">Horario</label>
                <input
                  type="time"
                  value={timeValue}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':');
                    setSnapshotConfigDraft((prev) => prev ? { ...prev, hour: Number(h || 0), minute: Number(m || 0) } : prev);
                  }}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold">Janela (dias)</label>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={snapshotConfigDraft?.windowDays ?? 7}
                  onChange={(e) => setSnapshotConfigDraft((prev) => prev ? { ...prev, windowDays: Number(e.target.value || 1) } : prev)}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}
                />
              </div>
            </div>
          </div>
          <div className={`flex items-center justify-end gap-2 p-4 border-t ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={closeSnapshotConfig}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveSnapshotConfig}
              disabled={snapshotConfigSaving || snapshotConfigLoading}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'bg-vision-activeRed border-vision-activeRed text-white hover:bg-vision-activeRed/90'
                  : 'bg-[#294b29] border-[#294b29] text-white hover:bg-[#1a301a]'
              }`}
            >
              {snapshotConfigSaving ? 'Salvando...' : snapshotConfigLoading ? 'Carregando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
      );
    };

  const renderManualSnapshotModal = () => {
    if (serviceTab !== 'GERAL' || !manualSnapshotOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className={`w-full max-w-md rounded-2xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className={`flex items-center justify-between gap-4 p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <div>
              <p className="text-sm font-semibold">Snapshot manual</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Enfileira um snapshot de credenciamento para a data escolhida.
              </p>
            </div>
            <button
              type="button"
              onClick={closeManualSnapshot}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Fechar"
            >
              Fechar
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="space-y-1">
              <label className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Data</label>
              <input
                type="date"
                value={manualSnapshotDate}
                onChange={(e) => setManualSnapshotDate(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}
              />
            </div>
            {manualSnapshotError ? (
              <p className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{manualSnapshotError}</p>
            ) : null}
          </div>
          <div className={`flex items-center justify-end gap-2 p-4 border-t ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={closeManualSnapshot}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submitManualSnapshot}
              disabled={manualSnapshotLoading}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                darkMode
                  ? 'bg-vision-activeRed border-vision-activeRed text-white hover:bg-vision-activeRed/90'
                  : 'bg-[#294b29] border-[#294b29] text-white hover:bg-[#1a301a]'
              } ${manualSnapshotLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {manualSnapshotLoading ? 'Enfileirando...' : 'Tirar snapshot'}
            </button>
          </div>
        </div>
      </div>
    );
  };


  useEffect(() => {
    coordinationSummaryRef.current = coordinationStatusSummary;
  }, [coordinationStatusSummary]);

  const normalizeAgencyStatus = (status: string, activeFlag?: boolean | null): AssociatedAgency['status'] =>
    resolveImobiliariaStatus(status, activeFlag);

  const getAgencyStatusBucket = (status: ImobiliariaStatus): FilterStatus => {
    switch (status) {
      case 'EM_ANALISE':
        return 'analysis';
      case 'CREDENCIANDO':
        return 'credentialing';
      case 'APROVADA':
        return 'active';
      case 'REJEITADA':
      case 'SUSPENSA':
      default:
        return 'blocked';
    }
  };

  const normalizeTag = useCallback((value: string) => value.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase(), []);
  const fetchAgencyTags = useCallback(async () => {
    try {
      const response = await fetch(`${CORE_API}/imobiliarias/tags`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar tags');
      const data = await response.json();
      setAvailableAgencyTags(Array.isArray(data?.tags) ? data.tags : []);
    } catch (err) {
      console.error('Erro ao carregar tags de imobiliarias', err);
      setAvailableAgencyTags([]);
    }
  }, []);

  useEffect(() => {
    if (viewMode !== 'detail') return;
    fetchAgencyTags();
  }, [fetchAgencyTags, viewMode]);

  const fetchCoordinationData = useCallback(
    async (productId: number, options?: { force?: boolean; dateFrom?: string; dateTo?: string; mode?: 'live' | 'snapshot' }) => {
      const statuses = 'aguardando_atendimento,em_atendimento,aguardando_resposta_corretor,encerrado,descartado';
      const limit = 15;
      const agenciesOffset = 0;
      const agenciesLimit = agenciesPageSize === 'all' ? 5000 : 1000;
      const dateFrom = options?.dateFrom;
      const dateTo = options?.dateTo;
      const mode = options?.mode || 'live';
      const cacheKey = `${mode}:coordination:${productId}:${dateFrom || ''}:${dateTo || ''}:${statuses}:${limit}:${agenciesLimit}:${agenciesOffset}`;
      if (!options?.force) {
        const cached = coordinationCacheRef.current.get(cacheKey);
        if (cached) {
          if (Date.now() - cached.ts < WIDGET_CACHE_TTL_MS) {
            if (mode === 'snapshot') {
              setSnapshotAgencies(cached.data.agencies);
              setSnapshotTickets(cached.data.tickets);
              setSnapshotTicketRanking(cached.data.ranking);
              setSnapshotCategories(cached.data.categories);
              setSnapshotAgenciesTotal(cached.data.agenciesTotal);
              setSnapshotCoordinationSummary(cached.data.agenciesSummary);
              setSnapshotCoordinationLastUpdated(new Date(cached.ts));
            } else {
              setAssociatedAgencies(cached.data.agencies);
              setTickets(cached.data.tickets);
              setTicketRanking(cached.data.ranking);
              setCategories(cached.data.categories);
              setAgenciesTotal(cached.data.agenciesTotal);
              setCoordinationStatusSummary(cached.data.agenciesSummary);
              setCoordinationLastUpdated(new Date(cached.ts));
            }
            setIsLoadingTickets(false);
            setTicketRankingLoading(false);
            setTicketError('');
            setTicketRankingError(null);
            return;
          }
          coordinationCacheRef.current.delete(cacheKey);
        }
      }
      try {
        setIsLoadingTickets(true);
        setTicketRankingLoading(true);
        setTicketError('');
        setTicketRankingError(null);
      const params = new URLSearchParams({
        statuses,
        limit: String(limit),
        agenciesLimit: String(agenciesLimit),
        agenciesOffset: String(agenciesOffset),
      });
      if (mode === 'snapshot') {
        params.set('mode', 'snapshot');
      }
      if (dateFrom) {
        params.set('from', dateFrom);
      }
      if (dateTo) {
        params.set('to', dateTo);
      }
        const response = await fetch(`${CORE_API}/relatorios/produtos/${productId}/coordination?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error('Falha ao carregar dados de coordenação');
        const data = await response.json();

        const items: any[] = data?.imobiliarias || [];
        const mapped: AssociatedAgency[] = items.map((item) => ({
          id: item.id,
          name: item.nome || item.nome_fantasia || 'Sem nome',
          sold: item.vendas || 0,
          vgv: item.vgv || '-',
          status: normalizeAgencyStatus(item.status, item.ativa),
          city: item.cidade ?? null,
          state: item.estado ?? null,
          tags: (item.tags || '')
            .split(',')
            .map((tag: string) => tag.trim())
            .filter(Boolean),
        }));

        const nextTickets = data?.atendimentos || [];
        const ranking: TicketRankingItem[] = (data?.ranking || []).map((r: any) => ({
          categoryId: r.categoriaId ?? null,
          category: r.categoriaNome || 'Indefinido',
          count: Number(r.total) || 0,
        }));
        const nextCategories = (data?.categorias || []).map((c: any) => ({ id: c.id, nome: c.nome, cor: c.cor || null }));
        const nextAgenciesTotal = Number(data?.imobiliariasTotal) || mapped.length;
        const summary = data?.imobiliariasSummary;
        const fallbackSummary = {
          analysis: mapped.filter((agency) => getAgencyStatusBucket(agency.status) === 'analysis').length,
          credentialing: mapped.filter((agency) => getAgencyStatusBucket(agency.status) === 'credentialing').length,
          active: mapped.filter((agency) => getAgencyStatusBucket(agency.status) === 'active').length,
          blocked: mapped.filter((agency) => getAgencyStatusBucket(agency.status) === 'blocked').length,
          total: nextAgenciesTotal,
        };
        const nextSummary = summary
          ? {
              analysis: Number(summary.analysis) || 0,
              credentialing: Number(summary.credentialing) || 0,
              active: Number(summary.active) || 0,
              blocked: Number(summary.blocked) || 0,
              total: Number(summary.total) || nextAgenciesTotal,
            }
          : fallbackSummary;

        if (mode === 'snapshot') {
          setSnapshotAgencies(mapped);
          setSnapshotTickets(nextTickets);
          setSnapshotTicketRanking(ranking);
          setSnapshotCategories(nextCategories);
          setSnapshotAgenciesTotal(nextAgenciesTotal);
          setSnapshotCoordinationSummary(nextSummary);
          setSnapshotCoordinationLastUpdated(new Date());
        } else {
          setAssociatedAgencies(mapped);
          setTickets(nextTickets);
          setTicketRanking(ranking);
          setCategories(nextCategories);
          setAgenciesTotal(nextAgenciesTotal);
          setCoordinationStatusSummary(nextSummary);
          coordinationSummaryRef.current = nextSummary;
          setCoordinationLastUpdated(new Date());
        }

        coordinationCacheRef.current.set(cacheKey, {
          ts: Date.now(),
          data: {
            agencies: mapped,
            tickets: nextTickets,
            ranking,
            categories: nextCategories,
            agenciesTotal: nextAgenciesTotal,
            agenciesSummary: nextSummary,
          },
        });
      } catch (e) {
        console.error('Erro ao buscar dados de coordenação', e);
        if (mode === 'snapshot') {
          setSnapshotAgencies([]);
          setSnapshotTickets([]);
          setSnapshotTicketRanking([]);
          setSnapshotCategories([]);
          setSnapshotAgenciesTotal(0);
        } else {
          setAssociatedAgencies([]);
          setTickets([]);
          setTicketRanking([]);
          setCategories([]);
          setAgenciesTotal(0);
        }
        setTicketError('Não foi possível carregar os atendimentos.');
        setTicketRankingError('Não foi possível carregar o ranking de chamados.');
      } finally {
        setIsLoadingTickets(false);
        setTicketRankingLoading(false);
      }
    },
    [agenciesPageSize]
  );

  const fetchCoordinationSummary = useCallback(
    async (productId: number, options?: { force?: boolean }) => {
      const cacheKey = `live-summary:${productId}`;
      if (!options?.force) {
        const cached = coordinationSummaryCacheRef.current.get(cacheKey);
        if (cached && Date.now() - cached.ts < WIDGET_CACHE_TTL_MS) {
          setCoordinationLiveSummary(cached.summary);
          setCoordinationLiveUpdatedAt(new Date(cached.ts));
          return;
        }
      }
      try {
        const response = await fetch(`${CORE_API}/relatorios/produtos/${productId}/coordination-summary`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error('Falha ao carregar resumo de credenciamento');
        const data = await response.json();
        const summary = data?.summary || {};
        const nextSummary = {
          analysis: Number(summary.analysis) || 0,
          credentialing: Number(summary.credentialing) || 0,
          active: Number(summary.active) || 0,
          blocked: Number(summary.blocked) || 0,
          total: Number(summary.total) || 0,
        };
        setCoordinationLiveSummary(nextSummary);
        setCoordinationLiveUpdatedAt(new Date());
        coordinationSummaryCacheRef.current.set(cacheKey, { ts: Date.now(), summary: nextSummary });
      } catch (err) {
        console.error('Erro ao carregar resumo de credenciamento', err);
        setCoordinationLiveSummary(EMPTY_COORDINATION_SUMMARY);
        setCoordinationLiveUpdatedAt(null);
      }
    },
    []
  );

    const mapApiProductToProduct = (product: ProductApi | any): Product => ({
      id: product.id,
      name: product.nome,
      builder: [product.cidade, product.estado].filter(Boolean).join(' / ') || '',
      status: product.ativo ? 'active' : 'blocked',
      phase: (product as any).fase || product.fase,
      praca: (product as any).praca || (product as any).praca_nome || (product as any).pracaNome || '',
      pracaNome: (product as any).praca_nome || (product as any).pracaNome || (product as any).praca || '',
    });

    const fetchProductById = async (id: number): Promise<Product | null> => {
      try {
        const response = await fetch(`${CORE_API}/produtos/${id}`, { headers: { ...getAuthHeaders() } });
        if (!response.ok) return null;
        const data = await response.json();
        const apiProduct = data?.produto;
        if (!apiProduct?.id) return null;
        return mapApiProductToProduct(apiProduct);
      } catch (err) {
        console.error('Erro ao buscar produto', err);
        return null;
      }
    };

    useEffect(() => {
      const fetchProducts = async () => {
      if (viewMode !== 'list') return;
      if (initialProductId) return;
      if (typeof window !== 'undefined' && new RegExp('^/produtos/\\\\d+').test(window.location.pathname)) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${CORE_API}/produtos`, { headers: { ...getAuthHeaders() } });
        if (!response.ok) throw new Error(`Falha ao carregar dados: ${response.statusText}`);
        const data = await response.json();
        const apiProducts: ProductApi[] = data?.produtos ?? [];
        const mapped: Product[] = apiProducts.map(mapApiProductToProduct);
        setProducts(mapped);
      } catch (err) {
        console.error('Erro ao buscar produtos:', err);
        setError('Não foi possível carregar a lista de produtos.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [viewMode, initialProductId]);

  const fetchProductServices = async (productId: number) => {
    setServicesLoaded(false);
    try {
      const response = await fetch(`${CORE_API}/produtos/${productId}/servicos`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar serviços do produto');
      const data = await response.json();
      const enabled = (data?.servicos || [])
        .filter((service: any) => service.habilitado)
        .map((service: any) => service.codigo as ServiceCode);
      setActiveServices(enabled);
    } catch (err) {
      console.error('Erro ao buscar serviços do produto', err);
      setActiveServices([]);
    } finally {
      setServicesLoaded(true);
    }

  };

  useEffect(() => {
    loadSnapshotCardPrefs(selectedProduct?.id);
  }, [loadSnapshotCardPrefs, selectedProduct?.id]);

  const fetchPipelineSnapshots = useCallback(async (productId: number, options?: { force?: boolean }) => {
    const compareDays = diffDays(widgetDateFrom, widgetDateTo);
    const baseDate = widgetDateTo || toDateOnlyString(new Date());
    const cacheKey = `pipeline:${productId}:${widgetDateFrom || ''}:${baseDate}:${compareDays}`;
    if (!options?.force) {
      const cached = pipelineCacheRef.current.get(cacheKey);
      if (cached) {
        if (Date.now() - cached.ts < WIDGET_CACHE_TTL_MS) {
          setPipelineSnapshot(cached.data.snapshot);
          setPipelineCompare(cached.data.compare);
          setPipelineSnapshotDate(cached.data.snapshotDate);
          setPipelineCompareDate(cached.data.compareDate);
          setPipelineDelta(cached.data.delta || null);
          setPipelineSeries(cached.data.series || []);
          setPipelineSnapshotError(cached.data.error);
          setPipelineSnapshotLoading(false);
          return;
        }
        pipelineCacheRef.current.delete(cacheKey);
      }
    }
    setPipelineSnapshotLoading(true);
    try {
      const response = await fetch(`${CORE_API}/relatorios/produtos/${productId}/pipeline-view?from=${widgetDateFrom}&to=${baseDate}&compareDays=${compareDays}`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar indicadores de credenciamento');
      const data = await response.json();
      const snapshot = data?.current || null;
      const compare = data?.previous || null;
      const snapshotDate = data?.snapshotDate || null;
      const compareDate = data?.compareDate || null;
      const delta = data?.delta || null;
      const series = Array.isArray(data?.series) ? data.series : [];
      const error = snapshot ? null : 'Snapshot n??o encontrado para a data solicitada.';
      setPipelineSnapshot(snapshot);
      setPipelineCompare(compare);
      setPipelineSnapshotDate(snapshotDate);
      setPipelineCompareDate(compareDate);
      setPipelineDelta(delta);
      setPipelineSeries(series);
      setPipelineSnapshotError(error);
      pipelineCacheRef.current.set(cacheKey, {
        ts: Date.now(),
        data: {
          snapshot,
          compare,
          snapshotDate,
          compareDate,
          delta,
          series,
          error,
        },
      });
    } catch (err) {
      console.error('Erro ao buscar indicadores de credenciamento', err);
      setPipelineSnapshot(null);
      setPipelineCompare(null);
      setPipelineSnapshotDate(null);
      setPipelineCompareDate(null);
      setPipelineDelta(null);
      setPipelineSeries([]);
      setPipelineSnapshotError('Falha ao carregar os indicadores de credenciamento.');
    } finally {
      setPipelineSnapshotLoading(false);
    }
  }, [widgetDateFrom, widgetDateTo]);

  const fetchEngagementSnapshot = useCallback(async (productId: number, options?: { force?: boolean }) => {
    const cacheKey = `engagement:${productId}:${widgetDateFrom}:${widgetDateTo}`;
    if (!options?.force) {
      const cached = pipelineCacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.ts < WIDGET_CACHE_TTL_MS && cached.data.snapshot) {
        setEngagementSnapshot(cached.data.snapshot as any);
        setEngagementError(null);
        return;
      }
      if (cached && Date.now() - cached.ts >= WIDGET_CACHE_TTL_MS) {
        pipelineCacheRef.current.delete(cacheKey);
      }
    }
    setEngagementLoading(true);
    setEngagementError(null);
    try {
      const params = new URLSearchParams({
        scope: 'produto',
        scopeId: String(productId),
        from: widgetDateFrom,
        to: widgetDateTo,
      });
      const response = await fetch(`${CORE_API}/dashboard/engajamento?${params.toString()}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!response.ok) throw new Error('Falha ao carregar engajamento');
      const data = await response.json();
      setEngagementSnapshot(data);
      pipelineCacheRef.current.set(cacheKey, {
        ts: Date.now(),
        data: { snapshot: data, snapshotDate: data?.snapshotDate ?? null, compare: null, compareDate: null, delta: null, series: [], error: null },
      });
    } catch (err) {
      console.error('Erro ao carregar engajamento', err);
      setEngagementSnapshot(null);
      setEngagementError('Falha ao carregar engajamento.');
    } finally {
      setEngagementLoading(false);
    }
  }, [widgetDateFrom, widgetDateTo]);

  const submitManualSnapshot = useCallback(async () => {
    if (!selectedProduct?.id) return;
    if (!manualSnapshotDate) {
      setManualSnapshotError('Informe uma data valida.');
      return;
    }
    setManualSnapshotLoading(true);
    setManualSnapshotError(null);
    try {
      const response = await fetch(`${CORE_API}/produtos/${selectedProduct.id}/snapshot-pipeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ date: manualSnapshotDate }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Falha ao enfileirar snapshot.');
      }
      closeManualSnapshot();
      fetchPipelineSnapshots(selectedProduct.id, { force: true });
    } catch (err) {
      console.error('Erro ao enfileirar snapshot manual', err);
      setManualSnapshotError('Falha ao enfileirar snapshot.');
    } finally {
      setManualSnapshotLoading(false);
    }
  }, [manualSnapshotDate, selectedProduct?.id, fetchPipelineSnapshots]);

  const fetchAttendanceKpis = useCallback(async (productId: number) => {
    setAttendanceKpisLoading(true);
    setAttendanceKpisError(null);
    try {
      const range = getLastNDaysRange(7);
      const baseDate = widgetDateTo || range.to;
      const params = new URLSearchParams({
        from: range.from,
        to: baseDate,
        slaSeconds: '3600',
        mode: 'snapshot',
      });
      const response = await fetch(`${CORE_API}/relatorios/produtos/${productId}/atendimento-kpis?${params.toString()}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!response.ok) throw new Error('Falha ao carregar KPIs de atendimento');
      const data = await response.json();
      setAttendanceKpis(data);
    } catch (err) {
      console.error('Erro ao carregar KPIs de atendimento', err);
      setAttendanceKpis(null);
      setAttendanceKpisError('Falha ao carregar KPIs de atendimento.');
    } finally {
      setAttendanceKpisLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'detail' && selectedProduct?.id && serviceTab === 'GERAL') {
      fetchPipelineSnapshots(selectedProduct.id, { force: true });
    }
  }, [viewMode, selectedProduct?.id, serviceTab, widgetDateFrom, widgetDateTo, fetchPipelineSnapshots]);
  const handleRefreshCoordination = useCallback(async (productIdOrEvent?: number | React.SyntheticEvent) => {
    const targetId = typeof productIdOrEvent === 'number' ? productIdOrEvent : selectedProduct?.id;
    if (!targetId) return;
    setIsRefreshingCoordination(true);
    try {
      setTicketRankingLoading(true);
      setTicketError('');
      setTicketRankingError(null);
      await Promise.all([
        fetchCoordinationData(targetId, { force: true, mode: 'live' }),
        fetchCoordinationSummary(targetId, { force: true }),
        fetchPipelineSnapshots(targetId, { force: true }),
      ]);
    } catch (err) {
      console.error('Erro ao atualizar dados de coordenação', err);
    } finally {
      setIsRefreshingCoordination(false);
      setTicketRankingLoading(false);
    }
  }, [selectedProduct?.id, fetchCoordinationData, fetchCoordinationSummary, fetchPipelineSnapshots]);

  const applyChangeCategory = async () => {
    if (!selectedTicket) return;
    const selectedCatId = changeCategoryValue ? Number(changeCategoryValue) : null;
    setChangeCategoryLoading(true);
    try {
      const statusToKeep = selectedTicket.status || 'em_atendimento';
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status: statusToKeep, categoriaId: selectedCatId || undefined })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Falha ao atualizar categoria');
      }
      const cat = categories.find(c => c.id === selectedCatId);
      setSelectedTicket(prev => prev ? { ...prev, categoria_id: selectedCatId, categoria_nome: cat?.nome || prev.categoria_nome } : prev);
      setTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, categoria_id: selectedCatId, categoria_nome: cat?.nome || t.categoria_nome } : t));
      setChangeCategoryDirty(true);
      closeChangeCategoryModal();
    } catch (err) {
      console.error('Erro ao atualizar categoria do atendimento', err);
      alert((err as Error)?.message || 'Falha ao atualizar categoria');
    } finally {
      setChangeCategoryLoading(false);
    }
  };

  const fetchTicketMessages = async (ticket: Atendimento) => {
    setSelectedTicket(ticket);
    setIsLoadingMessages(true);
    setTicketMessages([]);
    setChangeCategoryValue(ticket.categoria_id ? String(ticket.categoria_id) : '');
    try {
      const response = await fetch(`${CORE_API}/atendimentos/${ticket.id}/mensagens?limit=200`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar mensagens');
      const data = await response.json();
      setTicketMessages(data?.mensagens || []);
    } catch (err) {
      console.error('Erro ao buscar mensagens do atendimento', err);
      setTicketMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const isRegistrationEnabled = (productId?: number) => {
    if (!productId) return false;
    const stored = registrationByProduct[productId];
    return stored === undefined ? true : stored;
  };

  const fetchRegistrationStatus = async (productId: number) => {
    try {
      const response = await fetch(`${CORE_API}/produtos/${productId}/credenciamento`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar credenciamento');
      const data = await response.json();
      const enabled = data?.enabled;
      setRegistrationByProduct(prev => ({ ...prev, [productId]: enabled === undefined ? true : !!enabled }));
      setSelectedProduct((current) =>
        current?.id === productId ? { ...current, registrationEnabled: enabled === undefined ? true : !!enabled } : current
      );
    } catch (err) {
      console.error('Erro ao buscar status de credenciamento', err);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem('va_product_registration_toggles');
      if (!stored) return;
      const parsed = JSON.parse(stored) as Record<string, boolean>;
      const entries = Object.entries(parsed);
      if (!entries.length) return;
      entries.forEach(([id, enabled]) => {
        const productId = Number(id);
        if (!productId) return;
        fetch(`${CORE_API}/produtos/${productId}/credenciamento`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ enabled: !!enabled }),
        }).catch(() => undefined);
      });
      localStorage.removeItem('va_product_registration_toggles');
    } catch {
      // ignore migration failures
    }
  }, []);

  const getRegistrationBase = () => {
    const tenantSlug =
      (typeof window !== 'undefined' && (localStorage.getItem('va_tenant_name') || localStorage.getItem('va_tenant_id'))) ||
      'tenant';
    const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : '';
    const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
    const base = `${protocol}//${host}${port}`;
    return { base, tenantSlug };
  };

  const getRegistrationLink = (productId?: number) => {
    if (!productId) return '';
    const { base, tenantSlug } = getRegistrationBase();
    return `${base}/${tenantSlug}/cadastro/${productId}`;
  };


  // --- ACTIONS ---

  const handleProductClick = async (id: number, options?: { skipNavigation?: boolean }) => {
    if (!options?.skipNavigation) {
      onNavigateToProduct?.(id);
    }
    setIsLoading(true);
    setIsLoadingDetail(true);
    try {
      // Fetch detailed profile (Single Object Mock)
      const response = await fetch('/mock/produtos/product_profiles.json');
      if (response.ok) {
        const profileTemplate: ProductProfile = await response.json();
        
        // Simulate network
        await new Promise(r => setTimeout(r, 600));
        
        // Find basic info from the list to merge (to keep consistent ID/Name in UI)
        const basicProduct = products.find(p => p.id === id) || await fetchProductById(id);

        if (basicProduct) {
          // Merge mock data with real selected item ID and Name
          const mergedProfile: ProductProfile = {
             ...profileTemplate,
             id: basicProduct.id,
             name: basicProduct.name,
             builder: basicProduct.builder,
             status: basicProduct.status,
             phase: basicProduct.phase,
             praca: basicProduct.praca,
             pracaNome: basicProduct.pracaNome,
             registrationEnabled: true,
             // Keep the detailed stats from the mock
          };

          setSelectedProduct(mergedProfile);
          setViewMode('detail');
          setAgencySearch(''); // Reset filter when opening new product
          setStatusFilters([]); // Reset status filter
          // setIsEditingSpecs(false); // Reset edit mode
          setActiveServices([]); // Reset services while loading
          setServicesLoaded(false);
            await fetchProductServices(id);
            await fetchRegistrationStatus(id);
        } else {
            setError('Produto nao encontrado.');
        }
      } else {
          console.error("Failed to load product profile mock");
      }
    } catch (e) {
      console.error("Erro ao carregar detalhes", e);
    } finally {
      setIsLoading(false);
      setIsLoadingDetail(false);
    }
  };

  const handleBackToList = () => {
    skipAutoOpenRef.current = true;
    // Atualiza URL (via prop) e evita reabrir detalhe
    if (onExitProduct) {
      onExitProduct();
    } else if (typeof window !== 'undefined') {
      window.history.pushState({}, '', '/produtos');
    }
    setViewMode('list');
    setSelectedProduct(null);
    setIsLoadingDetail(false);
    setAssociatedAgencies([]);
    setActiveServices([]);
    setServicesLoaded(false);
  };

  const handleServiceTabChange = useCallback((nextTab: typeof serviceTab) => {
    manualTabChangeRef.current = true;
    setServiceTab(nextTab);
  }, []);

  const handleToggleFilter = (status: FilterStatus) => {
    setStatusFilters((current) =>
      current.includes(status) ? current.filter((value) => value !== status) : [...current, status]
    );
  };

  const handleBulkSelectionToggle = () => {
    setBulkSelectionEnabled((current) => {
      const next = !current;
      if (next) {
        setSelectedAgencyIds(pagedAssociatedAgencies.map((agency) => agency.id));
      }
      return next;
    });
  };

  const handleBulkExportCsv = () => {
    if (!selectedAgencies.length) return;
    const toCsvValue = (value: string | number | null | undefined) => {
      const normalized = String(value ?? '');
      return `"${normalized.replace(/"/g, '""')}"`;
    };
    const header = ['ID', 'Nome', 'Status', 'Vendas', 'VGV', 'Cidade', 'Estado'];
    const rows = selectedAgencies.map((agency) => [
      agency.id,
      agency.name,
      AGENCY_STATUS_EXPORT_LABELS[getAgencyStatusBucket(agency.status)],
      agency.sold ?? 0,
      agency.vgv || '',
      agency.city || '',
      agency.state || '',
    ]);
    const csv = [header, ...rows].map((row) => row.map(toCsvValue).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `imobiliarias-selecionadas-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleBulkStatusApply = async () => {
    if (!selectedAgencyIds.length || !bulkStatus) return;
    setBulkActionLoading(true);
    try {
      const response = await fetch(`${CORE_API}/imobiliarias/bulk-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ ids: selectedAgencyIds, status: bulkStatus }),
      });
      if (!response.ok) throw new Error('Falha ao atualizar status em massa');
      setBulkActionOpen(false);
      setBulkSelectionEnabled(false);
      if (selectedProduct?.id) {
        fetchCoordinationData(selectedProduct.id, { force: true, mode: 'live' });
      }
    } catch (err) {
      console.error('Erro ao atualizar status em massa', err);
      alert((err as Error)?.message || 'Falha ao atualizar status em massa');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // const handleToggleStatusDetail = () => {
  //   if (!selectedProduct) return;
  //   const newStatus = selectedProduct.status === 'active' ? 'blocked' : 'active';
    
  //   // Update local state
  //   setSelectedProduct({ ...selectedProduct, status: newStatus });
    
  //   // Update list state (so it reflects when going back)
  //   setProducts(products.map(p => p.id === selectedProduct.id ? { ...p, status: newStatus } : p));
  // };

  // Specs Actions
  // const handleEditSpecs = () => {
  //   if (selectedProduct) {
  //     setTempSpecs({ ...selectedProduct.specs });
  //     setTempPhase(selectedProduct.phase || 'LANCAMENTO');
  //     setIsEditingSpecs(true);
  //   }
  // };

  // const handleCancelSpecs = () => {
  //   setIsEditingSpecs(false);
  //   setTempPhase(selectedProduct?.phase || 'LANCAMENTO');
  //   setTempSpecs(null);
  // };

  const handleToggleRegistration = async (productId: number) => {
    const next = !isRegistrationEnabled(productId);
    try {
      const res = await fetch(`${CORE_API}/produtos/${productId}/credenciamento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error('Falha ao atualizar credenciamento');
      setRegistrationByProduct((prev) => ({ ...prev, [productId]: next }));
      setSelectedProduct((current) =>
        current?.id === productId ? { ...current, registrationEnabled: next } : current
      );
    } catch (err) {
      console.error('Erro ao atualizar credenciamento', err);
    }
  };

  // const handleCopyRegistrationLink = async (productId: number) => {
  //   const enabled = isRegistrationEnabled(productId);
  //   const link = enabled ? getRegistrationLink(productId) : getDisabledRegistrationLink();
  //   if (!link) return;

  //   try {
  //     if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
  //       await navigator.clipboard.writeText(link);
  //     } else if (typeof document !== 'undefined') {
  //       const tempInput = document.createElement('input');
  //       tempInput.value = link;
  //       document.body.appendChild(tempInput);
  //       tempInput.select();
  //       document.execCommand('copy');
  //       document.body.removeChild(tempInput);
  //     }
  //     // setRegistrationCopyMessage(enabled ? 'Link copiado para a rea de transferncia.' : 'Link geral copiado para contato.');
  //     // setTimeout(() => setRegistrationCopyMessage(''), 2500);
  //   } catch (err) {
  //     console.error('Erro ao copiar link de cadastro', err);
  //     // setRegistrationCopyMessage('No foi possvel copiar o link agora.');
  //   }
  // };

  // const handleOpenRegistrationLink = (productId: number) => {
  //   const link = isRegistrationEnabled(productId) ? getRegistrationLink(productId) : getDisabledRegistrationLink();
  //   if (!link) return;
  //   if (typeof window !== 'undefined') {
  //     window.open(link, '_blank');
  //   }
  // };

  const availableTabs = useMemo(
    () =>
      [
        { key: 'Coordenacao', label: 'Coordenação', service: 'COORDENACAO' as ServiceCode },
        { key: 'GESTAO', label: 'Gestão Leads', service: 'GESTAO' as ServiceCode },
        { key: 'SDR', label: 'SDR', service: 'SDR' as ServiceCode },
        { key: 'PESQUISA', label: 'Pesquisa', service: 'PESQUISA' as ServiceCode },
      ].filter((tab) => !tab.service || activeServices.includes(tab.service)),
    [activeServices]
  );

  const pesquisaTree = useMemo<PesquisaNode[]>(
    () => [
      {
        name: '2025',
        updated: '05/01/2025',
        items: [
          {
            name: 'Q1',
            updated: '05/01/2025',
            items: [
              { name: 'Diagnostico-inicial.pdf', type: 'file', size: '2.1 MB', updated: '05/01/2025' },
              { name: 'Pesquisa-campo-SP.xlsx', type: 'file', size: '860 KB', updated: '02/01/2025' },
            ],
          },
          {
            name: 'Q2',
            updated: '15/04/2025',
            items: [
              { name: 'Painel-demanda-Q2.pdf', type: 'file', size: '1.4 MB', updated: '15/04/2025' },
            ],
          },
        ],
      },
      {
        name: 'Historico 2024',
        updated: '20/12/2024',
        items: [
          { name: 'Resumo-mercado-2024.pdf', type: 'file', size: '1.9 MB', updated: '20/12/2024' },
          { name: 'Base-dados-competidores.csv', type: 'file', size: '430 KB', updated: '10/11/2024' },
        ],
      },
    ],
    [selectedProduct?.id]
  );

  useEffect(() => {
    if (!servicesLoaded) return;
    if (!availableTabs.some((tab) => tab.key === serviceTab)) {
      setServiceTab((availableTabs[0]?.key as any) || 'Coordenacao');
    }
  }, [availableTabs, serviceTab, servicesLoaded]);

  const formatPhase = (phase?: string) => {
    const norm = (phase || '').toUpperCase();
    switch (norm) {
      case 'PRE_LANCAMENTO':
      case 'PRE-LANCAMENTO':
        return 'Pre-lancamento';
      case 'LANCAMENTO':
        return 'Lancamento';
      case 'EM_VENDAS':
        return 'Em vendas';
      case 'ESTOQUE':
        return 'Estoque';
      case '100% VENDIDO':
      case '100_VENDIDO':
        return '100% vendido';
      default:
        return norm || 'Sem fase';
    }
  };

  const phaseBadgeClasses = (phase?: string) => {
    const norm = (phase || '').toUpperCase();
    if (norm === 'PRE_LANCAMENTO' || norm === 'PRE-LANCAMENTO') {
      return darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700';
    }
    if (norm === 'LANCAMENTO') {
      return darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
    }
    if (norm === 'EM_VENDAS') {
      return darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700';
    }
    if (norm === 'ESTOQUE') {
      return darkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-800';
    }
    if (norm === '100% VENDIDO' || norm === '100_VENDIDO') {
      return darkMode ? 'bg-purple-900/40 text-purple-200' : 'bg-purple-100 text-purple-700';
    }
    return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700';
  };

  // const formatTicketStatus = (status: string) => {
  //   switch (status) {
  //     case 'aguardando_atendimento':
  //       return 'Aguardando atendimento';
  //     case 'em_atendimento':
  //       return 'Em atendimento';
  //     case 'aguardando_resposta_corretor':
  //       return 'Aguardando resposta do corretor';
  //     case 'encerrado':
  //       return 'Encerrado';
  //     default:
  //       return status;
  //   }
  // };

  const formatTicketDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
  };

  const formatDurationShort = (seconds?: number | null) => {
    if (seconds === null || seconds === undefined) return '--';
    const safe = Math.max(0, Number(seconds));
    if (!Number.isFinite(safe)) return '--';
    if (safe < 60) return `${Math.round(safe)}s`;
    const totalMinutes = Math.round(safe / 60);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const formatPercent = (value?: number | null) => {
    if (value === null || value === undefined) return '--';
    if (!Number.isFinite(value)) return '--';
    return `${Math.round(value * 100)}%`;
  };

  // const handleSaveSpecs = async () => {
  //   if (!selectedProduct || !tempSpecs) return;
  //   const previousProducts = [...products];
  //   const previousSelected = { ...selectedProduct };
  //   const faseToPersist = tempPhase || 'LANCAMENTO';

  //   try {
  //     setSelectedProduct({
  //       ...selectedProduct,
  //       specs: tempSpecs,
  //       phase: faseToPersist
  //     });
  //     setProducts(products.map((p) => p.id === selectedProduct.id ? { ...p, phase: faseToPersist } : p));

  //     const response = await fetch(`${CORE_API}/produtos/${selectedProduct.id}`, {
  //       method: 'PUT',
  //       headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  //       body: JSON.stringify({ fase: faseToPersist })
  //     });

  //     if (!response.ok) {
  //       throw new Error('Falha ao salvar');
  //     }
  //     const payload = await response.json();
  //     const updatedPhase = (payload?.produto?.fase) || faseToPersist;

  //     setSelectedProduct((current) =>
  //       current ? { ...current, phase: updatedPhase, specs: tempSpecs } : current
  //     );
  //     setProducts((current) =>
  //       current.map((p) => p.id === selectedProduct.id ? { ...p, phase: updatedPhase } : p)
  //     );
  //     setTempSpecs(null);
  //     setIsEditingSpecs(false);
  //   } catch (err) {
  //     console.error('Erro ao salvar ficha tecnica', err);
  //     setSelectedProduct(previousSelected);
  //     setProducts(previousProducts);
  //     setError('No foi possvel salvar a ficha tecnica agora.');
  //   }
  // };

  // --- FORM ACTIONS (List View) ---

  // const handleOpenModal = (product?: Product) => {
  //   if (product) {
  //     // Only editing basic info in this modal
  //     setFormData(product);
  //   } else {
  //     setFormData({ name: '', builder: '', status: 'active', phase: 'LANCAMENTO' });
  //   }
  //   setIsModalOpen(true);
  // };

  // const handleCloseModal = () => {
  //   setIsModalOpen(false);
  // };

  // const handleBlockProduct = async (e: React.MouseEvent, id: number) => {
  //   e.stopPropagation();

  //   const previous = [...products];
  //   const target = products.find((p) => p.id === id);
  //   if (!target) return;

  //   const nextStatus = target.status === 'active' ? 'blocked' : 'active';

  //   setProducts(products.map(p => {
  //      if (p.id === id) {
  //        return { ...p, status: nextStatus };
  //      }
  //      return p;
  //   }));

  //   try {
  //     await fetch(`${CORE_API}/produtos/${id}`, {
  //       method: 'PUT',
  //       headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  //       body: JSON.stringify({ ativo: nextStatus === 'active' }),
  //     });
  //   } catch (err) {
  //     console.error("Erro ao atualizar status do produto:", err);
  //     setProducts(previous);
  //     setError("No foi possvel atualizar o status do produto.");
  //   }
  // };

  const filteredAssociatedAgencies = useMemo(() => {
    const search = debouncedAgencySearch.toLowerCase();
    const selectedTags = selectedAgencyTags.map(normalizeTag);
    return coordinationAgencies.filter((agency) => {
      if (statusFilters.length > 0 && !statusFilters.includes(getAgencyStatusBucket(agency.status))) {
        return false;
      }
      if (selectedTags.length > 0) {
        const agencyTags = (agency.tags || []).map(normalizeTag);
        if (!agencyTags.length) return false;
        const matchAny = selectedTags.some((tag) => agencyTags.includes(tag));
        const matchAll = selectedTags.every((tag) => agencyTags.includes(tag));
        if (agencyTagsMode === 'all' && !matchAll) return false;
        if (agencyTagsMode === 'any' && !matchAny) return false;
      }
      if (!search) return true;
      return agency.name.toLowerCase().includes(search);
    });
  }, [agencyTagsMode, coordinationAgencies, debouncedAgencySearch, getAgencyStatusBucket, normalizeTag, selectedAgencyTags, statusFilters]);
  const tagFilteredCount = useMemo(() => {
    if (!selectedAgencyTags.length) return 0;
    const selectedTags = selectedAgencyTags.map(normalizeTag);
    return coordinationAgencies.filter((agency) => {
      if (statusFilters.length > 0 && !statusFilters.includes(getAgencyStatusBucket(agency.status))) {
        return false;
      }
      const agencyTags = (agency.tags || []).map(normalizeTag);
      if (!agencyTags.length) return false;
      const matchAny = selectedTags.some((tag) => agencyTags.includes(tag));
      const matchAll = selectedTags.every((tag) => agencyTags.includes(tag));
      return agencyTagsMode === 'all' ? matchAll : matchAny;
    }).length;
  }, [agencyTagsMode, coordinationAgencies, getAgencyStatusBucket, normalizeTag, selectedAgencyTags, statusFilters]);
  const sortedAssociatedAgencies = useMemo(() => {
    const parseVgv = (value: string) => {
      const cleaned = value.replace(/[^\d,.-]/g, '');
      if (!cleaned) return 0;
      const normalized = cleaned.replace(/\./g, '').replace(/,/g, '.');
      const number = Number(normalized);
      return Number.isFinite(number) ? number : 0;
    };
    const compare = (a: AssociatedAgency, b: AssociatedAgency) => {
      switch (agencySort.column) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'sold':
          return (a.sold || 0) - (b.sold || 0);
        case 'vgv':
          return parseVgv(a.vgv) - parseVgv(b.vgv);
        default:
          return 0;
      }
    };
    const sorted = [...filteredAssociatedAgencies].sort(compare);
    return agencySort.direction === 'asc' ? sorted : sorted.reverse();
  }, [agencySort, filteredAssociatedAgencies]);
  const statusFilterDetails = useMemo(() => {
    if (!statusFilters.length) return '';
    const counts = filteredAssociatedAgencies.reduce(
      (acc, agency) => {
        const bucket = getAgencyStatusBucket(agency.status);
        acc[bucket] += 1;
        acc.total += 1;
        return acc;
      },
      { analysis: 0, credentialing: 0, active: 0, blocked: 0, total: 0 } as Record<FilterStatus | 'total', number>
    );
    if (statusFilters.length > 1) {
      const labels = statusFilters.map((status) => FILTER_STATUS_LABELS[status]).join(', ');
      return `[${labels}]: ${counts.total}`;
    }
    const singleStatus = statusFilters[0];
    return `${FILTER_STATUS_LABELS[singleStatus]}: ${counts[singleStatus] || 0}`;
  }, [filteredAssociatedAgencies, statusFilters]);
  const tagFilterDetails = useMemo(() => {
    if (!selectedAgencyTags.length) return '';
    if (selectedAgencyTags.length === 1) return `Tag: ${selectedAgencyTags[0]}`;
    return `Tags: ${selectedAgencyTags.length}`;
  }, [selectedAgencyTags]);
  const activeFilterLabel = useMemo(() => {
    const parts = [];
    if (statusFilters.length) parts.push(statusFilterDetails);
    if (selectedAgencyTags.length) parts.push(tagFilterDetails);
    return parts.join(' | ');
  }, [statusFilterDetails, statusFilters, selectedAgencyTags, tagFilterDetails]);
  const coordinationStatusTotals = useMemo(() => ({
    analysis: coordinationSummary.analysis,
    credentialing: coordinationSummary.credentialing,
    active: coordinationSummary.active,
    blocked: coordinationSummary.blocked,
  }), [coordinationSummary]);

  const coordinationWeekly = useMemo(() => {
    const weeks = coordinationRange === '4w' ? 4 : 8;
    const base = Math.max(coordinationSummary.total || 0, 8);
    return Array.from({ length: weeks }).map((_, idx) => {
      const factor = 1 - idx * 0.08;
      const analysis = Math.max(0, Math.round(coordinationStatusTotals.analysis * factor));
      const credentialing = Math.max(0, Math.round(coordinationStatusTotals.credentialing * factor));
      const active = Math.max(0, Math.round(coordinationStatusTotals.active * (0.6 + factor * 0.4)));
      const discarded = Math.max(0, Math.round(coordinationStatusTotals.blocked * (0.4 + factor * 0.3)));
      const total = Math.max(1, Math.round((analysis + credentialing + active + discarded) || base * factor));
      return {
        label: `Semana ${weeks - idx}`,
        analysis,
        credentialing,
        active,
        discarded,
        total,
      };
    }).reverse();
  }, [coordinationRange, coordinationSummary.total, coordinationStatusTotals]);

  const coordinationKpis = useMemo(() => {
    const pipeline = coordinationStatusTotals.analysis + coordinationStatusTotals.credentialing + coordinationStatusTotals.active + coordinationStatusTotals.blocked;
    const conversion = pipeline ? Math.round((coordinationStatusTotals.active / pipeline) * 100) : 0;
    const backlog = coordinationStatusTotals.analysis + coordinationStatusTotals.credentialing;
    const slaRisk = Math.max(0, Math.round(backlog * 0.35));
    const avgActivationDays = pipeline ? Math.max(4, Math.min(15, 12 - Math.round(coordinationStatusTotals.active * 0.2))) : 10;
    return { conversion, backlog, slaRisk, avgActivationDays, pipeline };
  }, [coordinationStatusTotals]);

  const generalKpis = useMemo(() => {
    if (!pipelineSnapshot || !pipelineDelta) return [];
    const toDelta = (value?: { diff: number }) => {
      const diff = Number(value?.diff || 0);
      if (diff === 0) return { label: "estavel", tone: "neutral" as const };
      return diff > 0 ? { label: "+" + diff, tone: "up" as const } : { label: "" + diff, tone: "down" as const };
    };

    const conversionRate = Number(pipelineSnapshot.conversao_pct || 0);
    return [
      {
        id: "entries",
        label: "Novas imobiliarias (7d)",
        value: Number(pipelineSnapshot.entradas_7d || 0),
        unit: "",
        delta: toDelta(pipelineDelta.entradas_7d),
        helper: "Entradas no funil",
      },
      {
        id: "moves",
        label: "Mudancas de status (7d)",
        value: Number(pipelineSnapshot.mudancas_status_7d || 0),
        unit: "",
        delta: toDelta(pipelineDelta.mudancas_status_7d),
        helper: "Transicoes no pipeline",
      },
      {
        id: "approved",
        label: "Aprovadas (7d)",
        value: Number(pipelineSnapshot.aprovadas_7d || 0),
        unit: "",
        delta: toDelta(pipelineDelta.aprovadas_7d),
        helper: "Conclusoes positivas",
      },
      {
        id: "rejected",
        label: "Suspensas (7d)",
        value: Number(pipelineSnapshot.rejeitadas_7d || 0),
        unit: "",
        delta: toDelta(pipelineDelta.rejeitadas_7d),
        helper: "Saidas negativas",
      },
      {
        id: "analysis-time",
        label: "Tempo medio em analise",
        value: Number(pipelineSnapshot.avg_dias_analise || 0),
        unit: "dias",
        delta: toDelta(pipelineDelta.avg_dias_analise),
        helper: "Tempo ate mover de analise",
      },
      {
        id: "cred-time",
        label: "Tempo medio em credenciamento",
        value: Number(pipelineSnapshot.avg_dias_credenciamento || 0),
        unit: "dias",
        delta: toDelta(pipelineDelta.avg_dias_credenciamento),
        helper: "Tempo ate aprovar ou bloquear",
      },
      {
        id: "backlog",
        label: "Backlog acima de 7d",
        value: Number(pipelineSnapshot.backlog_acima_7d || 0),
        unit: "",
        delta: toDelta(pipelineDelta.backlog_acima_7d),
        helper: "Risco de SLA estourado",
      },
      {
        id: "conversion",
        label: "Convers?o do funil",
        value: conversionRate,
        unit: "%",
        delta: toDelta(pipelineDelta.conversao_pct),
        helper: "Aprovadas / total",
        progress: Math.min(100, Math.max(0, conversionRate)),
        progressColor: darkMode ? "#22c55e" : "#16a34a",
      },
    ];
  }, [pipelineSnapshot, pipelineDelta, darkMode]);

  const ticketRankingSummary = useMemo(() => {
    const ordered = [...coordinationRanking].sort((a, b) => b.count - a.count);
    const highest = ordered.length ? ordered[0].count : 0;
    return { ordered, highest };
  }, [coordinationRanking]);

  const closeChangeCategoryModal = () => {
    setChangeCategoryModalOpen(false);
    if (changeCategoryDirty && selectedProduct) {
      setChangeCategoryDirty(false);
        fetchCoordinationData(selectedProduct.id, { force: true, mode: 'live' });
    }
  };

  const changeCategoryModal = changeCategoryModalOpen && selectedTicket && (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`${darkMode ? 'bg-vision-darkGreen border border-vision-border text-white' : 'bg-white border border-gray-200 text-gray-900'} w-full max-w-md rounded-2xl shadow-2xl`}>
        <div className="p-4 border-b flex items-center justify-between">
          <h4 className="text-lg font-bold flex items-center gap-2">
            <Tag size={16} /> Alterar categoria
          </h4>
          <button onClick={closeChangeCategoryModal} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Atendimento #{selectedTicket.id} - {selectedTicket.contato_nome || 'Sem nome'}
          </p>
          <div className="space-y-2">
            <label className="text-sm font-semibold">Categoria</label>
            <select
              value={changeCategoryValue}
              onChange={e => setChangeCategoryValue(e.target.value)}
              className={`w-full px-3 py-2 rounded border outline-none text-sm ${darkMode ? 'bg-vision-surface border-vision-border text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            >
              <option value="">Selecione...</option>
              {coordinationCategories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.nome}</option>
              ))}
            </select>
          </div>
        </div>
        <div className={`p-4 border-t flex justify-end gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <button
            onClick={closeChangeCategoryModal}
            className={`${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            disabled={changeCategoryLoading}
          >
            Cancelar
          </button>
          <button
            onClick={applyChangeCategory}
            disabled={changeCategoryLoading || !changeCategoryValue}
            className={`px-4 py-2 rounded-lg text-white font-semibold transition-all ${changeCategoryLoading || !changeCategoryValue ? 'bg-gray-400 cursor-not-allowed' : darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-[#294b29] hover:bg-[#1a301a]'}`}
          >
            {changeCategoryLoading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );

  const closeBulkActionModal = () => {
    setBulkActionOpen(false);
  };

  const bulkActionModal = bulkActionOpen && (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`${darkMode ? 'bg-vision-darkGreen border border-vision-border text-white' : 'bg-white border border-gray-200 text-gray-900'} w-full max-w-md rounded-2xl shadow-2xl`}>
        <div className="p-4 border-b flex items-center justify-between">
          <h4 className="text-lg font-bold flex items-center gap-2">
            <Settings size={16} /> Acoes em massa
          </h4>
          <button onClick={closeBulkActionModal} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Selecionadas: {selectedAgencyIds.length}
          </p>
          <div className="space-y-2">
            <label className="text-sm font-semibold">Mudar status</label>
            <select
              value={bulkStatus}
              onChange={(event) => setBulkStatus(event.target.value)}
              className={`w-full px-3 py-2 rounded border outline-none text-sm ${darkMode ? 'bg-vision-surface border-vision-border text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            >
              <option value="">Selecione...</option>
              {IMOBILIARIA_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleBulkStatusApply}
              disabled={bulkActionLoading || !bulkStatus || !selectedAgencyIds.length}
              className={`px-4 py-2 rounded-lg text-white font-semibold transition-all ${bulkActionLoading || !bulkStatus || !selectedAgencyIds.length ? 'bg-gray-400 cursor-not-allowed' : darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-[#294b29] hover:bg-[#1a301a]'}`}
            >
              {bulkActionLoading ? 'Aplicando...' : 'Aplicar status'}
            </button>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold">Exportar</label>
            <button
              type="button"
              onClick={handleBulkExportCsv}
              disabled={!selectedAgencyIds.length}
              className={`w-full px-4 py-2 rounded-lg border text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                selectedAgencyIds.length
                  ? darkMode ? 'border-vision-border text-gray-100 hover:bg-vision-surface' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <FileText size={14} />
              Baixar CSV
            </button>
          </div>
        </div>
        <div className={`p-4 border-t flex justify-end ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
          <button
            onClick={closeBulkActionModal}
            className={`${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );

  

  // useEffect(() => {
  //   if (typeof window === 'undefined') return;
  //   try {
  //     localStorage.setItem('va_product_registration_toggles', JSON.stringify(registrationToggles));
  //   } catch {}
  // }, [registrationToggles]);

  // Quando a rota tem um productId inicial, abre o detalhe ao carregar a lista
  useEffect(() => {
    if (!initialProductId) {
      // Quando param some, liberamos o auto-open novamente
      skipAutoOpenRef.current = false;
      autoOpenRef.current = null;
      return;
    }
    if (skipAutoOpenRef.current) return;
    if (selectedProduct?.id === initialProductId) return;
    if (autoOpenRef.current === initialProductId) return;
    autoOpenRef.current = initialProductId;
    handleProductClick(initialProductId, { skipNavigation: true });
  }, [initialProductId, products, selectedProduct?.id]);

  useEffect(() => {
    if (manualTabChangeRef.current) {
      manualTabChangeRef.current = false;
      return;
    }
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (lastRouteRef.current === pathname) {
        return;
      }
      lastRouteRef.current = pathname;
    }
    const route = parseProductRoute();
    if (!route?.productId) return;
    if (!initialProductId && viewMode === 'detail' && !selectedProduct?.id && !skipAutoOpenRef.current) {
      handleProductClick(route.productId, { skipNavigation: true });
    }
    const section = route.section === 'coordenação' ? 'coordenacao' : route.section;
    let nextTab: typeof serviceTab | null = null;
    if (section === 'coordenacao') nextTab = 'Coordenacao';
    if (section === 'gestao') nextTab = 'GESTAO';
    if (section === 'sdr') nextTab = 'SDR';
    if (section === 'pesquisa') nextTab = 'PESQUISA';
    if (section === 'snapshot' || section === 'geral' || !section) {
      if (!servicesLoaded) return;
      nextTab = (availableTabs[0]?.key as any) || 'Coordenacao';
    }
    if (!nextTab) return;
    if (servicesLoaded && !availableTabs.some((tab) => tab.key === nextTab)) {
      nextTab = (availableTabs[0]?.key as any) || 'Coordenacao';
    }
    if (nextTab && nextTab !== serviceTab) {
      setServiceTab(nextTab);
    }
    if (nextTab === 'Coordenacao') {
      if (route.sub === 'atendimentos' && coordinationTab !== 'atendimentos') {
        setCoordinationTab('atendimentos');
      } else if (coordinationTab !== 'credenciamento' && route.sub !== 'atendimentos') {
        setCoordinationTab('credenciamento');
      }
    }
  }, [viewMode, selectedProduct?.id, initialProductId, serviceTab, coordinationTab, availableTabs, servicesLoaded]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!selectedProduct?.id) return;
    let next = buildProductRoute(selectedProduct.id);
    if (serviceTab === 'Coordenacao') {
      next = buildProductRoute(selectedProduct.id, 'coordenacao', coordinationTab);
    } else if (serviceTab === 'GESTAO') {
      next = buildProductRoute(selectedProduct.id, 'gestao');
    } else if (serviceTab === 'SDR') {
      next = buildProductRoute(selectedProduct.id, 'sdr');
    } else if (serviceTab === 'PESQUISA') {
      next = buildProductRoute(selectedProduct.id, 'pesquisa');
    }
    if (window.location.pathname !== next) {
      window.history.pushState({}, '', next);
      lastRouteRef.current = next;
    }
  }, [serviceTab, coordinationTab, selectedProduct?.id]);

  useEffect(() => {
    if (viewMode === 'detail' && selectedProduct?.id && serviceTab === 'Coordenacao') {
      fetchCoordinationData(selectedProduct.id, { mode: 'live' });
    } else if (viewMode !== 'detail') {
      setTickets([]);
      setTicketRanking([]);
    }
  }, [viewMode, selectedProduct?.id, serviceTab, fetchCoordinationData]);

  useEffect(() => {
    setSelectedAgencyTags([]);
    setAgencyTagsMode('any');
  }, [selectedProduct?.id]);

  useEffect(() => {
    if (viewMode === 'detail' && selectedProduct?.id && serviceTab === 'GERAL') {
      const targetId = selectedProduct.id;
      fetchPipelineSnapshots(targetId);
      if (isSnapshotCardEnabled('snapshot_engajamento')) {
        fetchEngagementSnapshot(targetId);
      }
      const coordinationEnabled = isSnapshotCardEnabled('snapshot_credenciamento') && isWidgetAvailable('coordination');
      const atendimentosEnabled = isSnapshotCardEnabled('snapshot_atendimentos') && isWidgetAvailable('attendance_kpis');
      if (atendimentosEnabled) {
        fetchAttendanceKpis(targetId);
      }
      if (!coordinationEnabled) {
        return;
      }
      const fetchSummary = () => {
        fetchCoordinationSummary(targetId);
      };
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        (window as any).requestIdleCallback(fetchSummary, { timeout: 1200 });
      } else {
        setTimeout(fetchSummary, 0);
      }
    }
  }, [
    viewMode,
    selectedProduct?.id,
    serviceTab,
    fetchCoordinationSummary,
    fetchPipelineSnapshots,
    fetchEngagementSnapshot,
    fetchAttendanceKpis,
    isSnapshotCardEnabled,
    isWidgetAvailable,
  ]);

  useEffect(() => {
    if (serviceTab === 'Coordenacao') {
      setTicketsPage(1);
    }
  }, [serviceTab, selectedProduct?.id, ticketCategoryFilter]);

  const normalizeLabel = (value?: string | null) => (value || '').trim().toLowerCase();

  const filteredTickets = useMemo(() => {
    if (!ticketCategoryFilter) return coordinationTickets;
    const label = normalizeLabel(ticketCategoryFilter.label);
    const isIndefinido = label === "indefinido";
    if (ticketCategoryFilter.id != null) {
      return coordinationTickets.filter((t) => {
        if (t.categoria_id === ticketCategoryFilter.id) return true;
        if (isIndefinido && !t.categoria_id) return true;
        return normalizeLabel(t.categoria_nome) === label;
      });
    }
    return coordinationTickets.filter((t) => !t.categoria_id || normalizeLabel(t.categoria_nome) === label);
  }, [coordinationTickets, ticketCategoryFilter]);

  const ticketsPerPage = 5;
  const ticketPages = Math.ceil(filteredTickets.length / ticketsPerPage);
  const ticketPageIndex = Math.max(0, ticketsPage - 1);
  const pagedTickets = filteredTickets.slice(
    ticketPageIndex * ticketsPerPage,
    ticketPageIndex * ticketsPerPage + ticketsPerPage
  );

  useEffect(() => {
    if (!ticketPages) return;
    if (ticketsPage > ticketPages) setTicketsPage(ticketPages);
  }, [ticketPages, ticketsPage]);

  useEffect(() => {
    if (serviceTab === 'Coordenacao' || serviceTab === 'GERAL') {
      setAgenciesPage(1);
    }
  }, [serviceTab, selectedProduct?.id, statusFilters, agencySearch, agenciesPageSize]);
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedAgencySearch(agencySearch.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [agencySearch]);
  useEffect(() => {
    if (agenciesPageSize === 'all') return;
    if (!customAgenciesPageSizeActive) {
      setCustomAgenciesPageSize(String(agenciesPageSize));
    }
  }, [agenciesPageSize, customAgenciesPageSizeActive]);

  const agenciesTotalCount = filteredAssociatedAgencies.length;
  const agenciesPages = agenciesPageSize === 'all' ? (agenciesTotalCount ? 1 : 0) : Math.ceil(agenciesTotalCount / agenciesPageSize);
  const agenciesPageIndex = Math.max(0, agenciesPage - 1);
  const pagedAssociatedAgencies =
    agenciesPageSize === 'all'
      ? sortedAssociatedAgencies
      : sortedAssociatedAgencies.slice(
          agenciesPageIndex * agenciesPageSize,
          agenciesPageIndex * agenciesPageSize + agenciesPageSize
        );
  const selectedAgencies = useMemo(
    () => filteredAssociatedAgencies.filter((agency) => selectedAgencyIds.includes(agency.id)),
    [filteredAssociatedAgencies, selectedAgencyIds]
  );
  useEffect(() => {
    if (!bulkSelectionEnabled) return;
    setSelectedAgencyIds((current) =>
      current.filter((id) => pagedAssociatedAgencies.some((agency) => agency.id === id))
    );
  }, [pagedAssociatedAgencies, bulkSelectionEnabled]);

  useEffect(() => {
    if (!agenciesPages) return;
    if (agenciesPage > agenciesPages) setAgenciesPage(agenciesPages);
  }, [agenciesPages, agenciesPage]);

  // Helper for Status Badge
  const StatusBadge = ({ status }: { status: ImobiliariaStatus }) => {
      const bucket = getAgencyStatusBucket(status);
      let colorClass = '';
      let label = '';
      
      switch(bucket) {
          case 'analysis': 
              colorClass = darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800';
              label = 'Analise';
              break;
          case 'credentialing':
              colorClass = darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
              label = 'Credenciando';
              break;
          case 'active':
              colorClass = darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-900';
              label = 'Ativas';
              break;
          case 'blocked':
              colorClass = darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-800';
              label = 'Suspensas';
              break;
          default:
              colorClass = 'bg-gray-100 text-gray-800';
              label = bucket;
      }

      return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
              {label}
          </span>
      );
  };

  // --- RENDER: LIST VIEW ---
  if (viewMode === 'list') {
    const filteredProducts = products.filter((p) => {
      const q = productSearch.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        p.builder.toLowerCase().includes(q) ||
        (p.pracaNome || '').toLowerCase().includes(q) ||
        (p.praca || '').toLowerCase().includes(q) ||
        String(p.phase || '').toLowerCase().includes(q)
      );
    });

    return (
      <>
      {changeCategoryModal}
      {bulkActionModal}
      <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-vision-green'}`}>
              Catálogo de Produtos
            </h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Gerencie os empreendimentos e acompanhe resultados.</p>
                      </div>
          
          {/* ONLY ADMIN CAN CREATE NEW PRODUCTS */}
        </div>

        <div className={`
          rounded-xl border shadow-sm overflow-hidden min-h-[400px] flex flex-col
          ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
        `}>
          
          <div className={`p-4 border-b flex items-center gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
             <div className="relative flex-1 max-w-sm">
               <Search className={`absolute left-3 top-2.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={18} />
               <input 
                  type="text" 
                  placeholder="Buscar empreendimento..." 
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className={`
                    w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 outline-none text-sm transition-all
                    ${darkMode 
                      ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-800 focus:ring-[#294b29] focus:border-transparent'
                    }
                  `}
      />
             </div>
          </div>

          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-gray-500 animate-in fade-in">
               <Loader2 className={`w-10 h-10 animate-spin mb-4 ${darkMode ? 'text-vision-green' : 'text-[#294b29]'}`} />
               <p>Carregando produtos...</p>
                      </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-red-500 animate-in fade-in">
               <AlertTriangle className="w-10 h-10 mb-4" />
               <p>{error}</p>
                      </div>
          ) : products.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-gray-500 animate-in fade-in">
              <Ban className="w-10 h-10 mb-4" />
              <p>Nenhum produto ativo no momento.</p>
                      </div>
          ) : (
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className={`text-xs uppercase tracking-wider ${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                    <th className="px-6 py-4 font-semibold">Produto</th>
                    <th className="px-6 py-4 font-semibold">Praça</th>
                    <th className="px-6 py-4 font-semibold text-center">Status</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-200'}`}>
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-6 text-center text-sm text-gray-500">
                        Nenhum produto encontrado.
                      </td>
                    </tr>
                  ) : filteredProducts.map((product) => (
                    <tr 
                      key={product.id} 
                      className={`
                        transition-colors group
                        ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-50'}
                      `}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                                    <div className={`
                            w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm
                            ${darkMode ? 'bg-vision-surface text-gray-300' : 'bg-green-100 text-vision-green'}
                          `}>
                            <Package size={20} />
                          </div>
                          {/* CLICKABLE NAME */}
                          <button 
                            onClick={() => handleProductClick(product.id)}
                            className={`font-medium hover:underline text-left ${darkMode ? 'text-gray-200 hover:text-white' : 'text-gray-900 hover:text-[#294b29]'}`}
                          >
                            {product.name}
                          </button>
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <div className="flex items-center gap-2">
                           <Building size={16} />
                           {product.pracaNome || product.praca || 'Sem praça'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`
                           inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                           ${phaseBadgeClasses(product.phase)}
                        `}>
                          {formatPhase(product.phase)}
                        </span>
                      </td>
                </tr>
              ))}
            </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
      </>
    );
  }

  // --- RENDER: DETAIL VIEW (PRODUCT DASHBOARD) ---
  if (viewMode === 'detail' && selectedProduct) {
      
    const registrationEnabled = isRegistrationEnabled(selectedProduct.id);
    const registrationLink = getRegistrationLink(selectedProduct.id);
      
    // Count agencies by status (prefer server summary so filters don't zero other buckets)
    const hasSummary = Number(coordinationSummary.total) > 0;
    const agencyCounts = hasSummary
      ? {
          analysis: coordinationSummary.analysis,
          credentialing: coordinationSummary.credentialing,
          active: coordinationSummary.active,
          blocked: coordinationSummary.blocked,
        }
      : {
          analysis: coordinationAgencies.filter(a => getAgencyStatusBucket(a.status) === 'analysis').length,
          credentialing: coordinationAgencies.filter(a => getAgencyStatusBucket(a.status) === 'credentialing').length,
          active: coordinationAgencies.filter(a => getAgencyStatusBucket(a.status) === 'active').length,
          blocked: coordinationAgencies.filter(a => getAgencyStatusBucket(a.status) === 'blocked').length
        };

    

    const renderPesquisaNodes = (nodes: PesquisaNode[], depth = 0) =>
      nodes.map((node, idx) => {
        const isFile = node.type === 'file';
        const padding = 12 + depth * 12;
        return (
          <div key={`${node.name}-${idx}`} className="space-y-1">
            <div
              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${darkMode ? 'border-vision-border bg-vision-surface/60 text-gray-100' : 'border-gray-200 bg-white text-gray-800'}`}
              style={{ paddingLeft: padding }}
            >
              <div className="flex items-center gap-2">
                {isFile ? <FileText size={14} /> : <Folder size={14} />}
                <span className="font-medium">{node.name}</span>
              </div>
              <div className="flex items-center gap-3 text-xs opacity-70">
                {node.size && <span>{node.size}</span>}
                {node.updated && <span>{node.updated}</span>}
                {isFile && (
                  <button
                    className={`px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors ${darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-100'}`}
                    onClick={() => {}}
                  >
                    Download
                  </button>
                )}
              </div>
            </div>
            {node.items && node.items.length > 0 && (
              <div className="space-y-1">
                {renderPesquisaNodes(node.items, depth + 1)}
              </div>
            )}
          </div>
        );
      });

    const renderGeneralWidget = (key: string) => {
      if (key === 'coordination') {
        if (!isWidgetAvailable(key)) {
          return (
            <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-bold flex items-center gap-2">
                    <Building2 size={18} /> Credenciamento
                  </h3>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Servico COORDENACAO inativo para este produto.
                  </p>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold flex items-center gap-2">
                  <Building2 size={18} /> Credenciamento
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Panorama das imobiliárias por status.</p>
                </div>
              <div className={`${darkMode ? 'bg-vision-surface text-gray-100 border border-vision-border' : 'bg-gray-100 text-gray-700 border border-gray-200'} px-3 py-1 rounded-full text-xs font-semibold`}>
                Total: {agencyCounts.analysis + agencyCounts.credentialing + agencyCounts.active + agencyCounts.blocked}
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-4 mt-4">
              {(() => {
                const trendCard = SNAPSHOT_CHARTS.find((card) => card.id === 'snapshot_trend');
                const distributionCard = SNAPSHOT_CHARTS.find((card) => card.id === 'snapshot_distribution');
                const credenciamentoEnabled = isSnapshotCardEnabled('snapshot_credenciamento');

                  const palette = {
                    analysis: darkMode ? '#60a5fa' : '#3b82f6',
                    credentialing: darkMode ? '#facc15' : '#eab308',
                    active: darkMode ? '#22c55e' : '#16a34a',
                    blocked: darkMode ? '#ef4444' : '#dc2626',
                    glow: darkMode ? 'rgba(239, 68, 68, 0.35)' : 'rgba(239, 68, 68, 0.2)',
                    grid: darkMode ? '#223022' : '#e5e7eb',
                    axis: darkMode ? '#9ca3af' : '#6b7280',
                  };

                const snapshotTotals = pipelineSnapshot
                  ? {
                      analysis: Number(pipelineSnapshot.em_analise || 0),
                      credentialing: Number(pipelineSnapshot.credenciando || 0),
                      active: Number(pipelineSnapshot.aprovadas || 0),
                      blocked: Number(pipelineSnapshot.suspensas || 0),
                    }
                  : null;
                const liveTotals = {
                  analysis: coordinationStatusTotals.analysis,
                  credentialing: coordinationStatusTotals.credentialing,
                  active: coordinationStatusTotals.active,
                  blocked: coordinationStatusTotals.blocked,
                };
                const hasLiveTotals =
                  liveTotals.analysis + liveTotals.credentialing + liveTotals.active + liveTotals.blocked > 0;
                const lastTotals = hasLiveTotals ? liveTotals : snapshotTotals;
                const todayLabel = toDateOnlyString(new Date());
                let trendData = pipelineSeries.map((item: any) => {
                  const emAnalise = Number(item?.em_analise ?? 0);
                  const credenciando = Number(item?.credenciando ?? 0);
                  const aprovadas = Number(item?.aprovadas ?? 0);
                  const suspensas = Number(item?.suspensas ?? 0);
                  const hasData = item?.hasData !== false;
                  return {
                    date: String(item?.date || ''),
                    em_analise: hasData ? emAnalise : null,
                    credenciando: hasData ? credenciando : null,
                    aprovadas: hasData ? aprovadas : null,
                    Suspensas: hasData ? suspensas : null,
                    gargalo: hasData ? (emAnalise + credenciando) : null,
                  };
                });
                if (trendData.length && lastTotals) {
                  const lastIndex = trendData.length - 1;
                  const lastSeries = pipelineSeries[lastIndex];
                  const lastPoint = trendData[lastIndex];
                  const missingValues = [
                    lastPoint?.em_analise,
                    lastPoint?.credenciando,
                    lastPoint?.aprovadas,
                    lastPoint?.Suspensas,
                  ].some((value) => value === null || value === undefined);
                  const shouldReplace =
                    lastSeries?.hasData === false ||
                    lastPoint?.date === todayLabel ||
                    missingValues;
                  if (shouldReplace) {
                    trendData[lastIndex] = {
                      ...lastPoint,
                      date: lastPoint?.date || todayLabel,
                      em_analise: lastTotals.analysis,
                      credenciando: lastTotals.credentialing,
                      aprovadas: lastTotals.active,
                      Suspensas: lastTotals.blocked,
                      gargalo: lastTotals.analysis + lastTotals.credentialing,
                    };
                  }
                }

                  const visibleCount = 7;
                  const totalPeriods = trendData.length;
                  const endValue = Math.max(0, totalPeriods - 1);
                  const startValue = Math.max(0, totalPeriods - visibleCount);

                const statusDistribution = [
                  { name: 'Analise', value: coordinationStatusTotals.analysis },
                  { name: 'Credenciando', value: coordinationStatusTotals.credentialing },
                  { name: 'Ativas', value: coordinationStatusTotals.active },
                  { name: 'Suspensas', value: coordinationStatusTotals.blocked },
                ];

                const totalDistribution = statusDistribution.reduce((sum, item) => sum + item.value, 0);

                const trendOption = {
                  backgroundColor: 'transparent',
                  tooltip: {
                    trigger: 'axis',
                      formatter: (params: any) => {
                        if (!Array.isArray(params)) return '';
                        const order = ['Suspensas', 'Analise', 'Credenciando', 'Ativas'];
                        const byName = new Map(params.map((item) => [item.seriesName, item]));
                        const rows = order
                          .map((name) => byName.get(name))
                          .filter(Boolean)
                          .map((item: any) => `${item.marker}${item.seriesName}: ${item.value}`)
                          .join('<br/>');
                        const rawDate = params[0]?.axisValueLabel || '';
                        const formattedDate = rawDate && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)
                          ? rawDate.split('-').reverse().join('/')
                          : rawDate;
                        const title = formattedDate ? `${formattedDate}<br/>` : '';
                        const total = order.reduce((sum, name) => sum + (Number(byName.get(name)?.value) || 0), 0);
                        const totalLine = `<br/>Total: ${total}`;
                        return `${title}${rows}${totalLine}`;
                      }
                    },
                    grid: { left: 12, right: 12, top: 24, bottom: 34, containLabel: true },
                    xAxis: {
                      type: 'category',
                      data: trendData.map((period) => period.date),
                      axisLine: { lineStyle: { color: palette.grid } },
                      axisLabel: { color: palette.axis, fontSize: 10 },
                      axisTick: { show: false },
                    },
                    yAxis: {
                      type: 'value',
                      axisLine: { show: false },
                      splitLine: { lineStyle: { color: palette.grid, opacity: 0.35 } },
                      axisLabel: { color: palette.axis, fontSize: 10 },
                    },
                    dataZoom: [
                      {
                        type: 'inside',
                        xAxisIndex: 0,
                        startValue,
                        endValue,
                        moveOnMouseWheel: true,
                        moveOnMouseMove: true,
                      },
                      {
                        type: 'slider',
                        xAxisIndex: 0,
                        height: 14,
                        bottom: 6,
                        startValue,
                        endValue,
                        backgroundColor: darkMode ? '#1f2a1f' : '#f3f4f6',
                        borderColor: darkMode ? '#2a3a2a' : '#e5e7eb',
                        fillerColor: darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
                        handleStyle: {
                          color: darkMode ? '#ef4444' : '#dc2626',
                          borderColor: darkMode ? '#ef4444' : '#dc2626',
                        },
                        textStyle: { color: palette.axis, fontSize: 10 },
                      },
                    ],
                    series: [
                      {
                        name: 'Ativas',
                        type: 'bar',
                        data: trendData.map((period) => period.aprovadas),
                        barWidth: 12,
                        stack: 'status',
                        itemStyle: { color: palette.active },
                      },
                      {
                        name: 'Credenciando',
                        type: 'bar',
                        data: trendData.map((period) => period.credenciando),
                        barWidth: 12,
                        stack: 'status',
                        itemStyle: { color: palette.credentialing },
                      },
                      {
                        name: 'Analise',
                        type: 'bar',
                        data: trendData.map((period) => period.em_analise),
                        barWidth: 12,
                        stack: 'status',
                        itemStyle: { color: palette.analysis },
                      },
                      {
                        name: 'Suspensas',
                        type: 'bar',
                        data: trendData.map((period) => period.Suspensas),
                        barWidth: 12,
                        stack: 'status',
                        itemStyle: { color: palette.blocked },
                      },
                      {
                        name: 'Gargalo',
                        type: 'line',
                        data: trendData.map((period) => period.gargalo),
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 6,
                        lineStyle: { width: 2, type: 'dashed', color: palette.blocked },
                        itemStyle: { color: palette.blocked },
                      },
                  ],
                };

                const donutOption = {
                  backgroundColor: 'transparent',
                  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
                  legend: {
                    bottom: 0,
                    textStyle: { color: palette.axis, fontSize: 10 },
                    itemWidth: 10,
                    itemHeight: 10,
                  },
                  graphic: [
                    {
                      type: 'text',
                      left: 'center',
                      top: '38%',
                      style: {
                        text: String(totalDistribution || 0),
                        fill: darkMode ? '#f3f4f6' : '#111827',
                        fontSize: 20,
                        fontWeight: 700,
                      },
                    },
                    {
                      type: 'text',
                      left: 'center',
                      top: '52%',
                      style: {
                        text: 'Total',
                        fill: palette.axis,
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 1,
                      },
                    },
                  ],
                  series: [
                    {
                      type: 'pie',
                      radius: ['50%', '78%'],
                      center: ['50%', '44%'],
                      padAngle: 2,
                      label: { show: false },
                      labelLine: { show: false },
                      itemStyle: {
                        borderWidth: 2,
                        borderRadius: 8,
                        borderColor: darkMode ? '#0f1b12' : '#ffffff',
                        shadowBlur: 12,
                        shadowColor: darkMode ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.12)',
                      },
                      data: [
                        { value: statusDistribution[0].value, name: statusDistribution[0].name, itemStyle: { color: palette.analysis } },
                        { value: statusDistribution[1].value, name: statusDistribution[1].name, itemStyle: { color: palette.credentialing } },
                        { value: statusDistribution[2].value, name: statusDistribution[2].name, itemStyle: { color: palette.active } },
                        { value: statusDistribution[3].value, name: statusDistribution[3].name, itemStyle: { color: palette.blocked } },
                      ],
                      emphasis: { scale: true, scaleSize: 10 },
                    },
                  ],
                };

                return (
                  <>
                    {credenciamentoEnabled && trendCard && (
                      <div className={`lg:col-span-8 rounded-xl border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center justify-between text-xs font-semibold mb-2">
                          <span>{trendCard.title}</span>
                          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{trendCard.subtitle}</span>
                        </div>
                        <ReactECharts option={trendOption} style={{ height: 220 }} />
                      </div>
                    )}
                    {credenciamentoEnabled && distributionCard && (
                      <div className={`lg:col-span-4 rounded-xl border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center justify-between text-xs font-semibold mb-2">
                          <span>{distributionCard.title}</span>
                          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{distributionCard.subtitle}</span>
                        </div>
                        <ReactECharts option={donutOption} style={{ height: 220 }} />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        );
      }
      if (key === 'pipeline_kpis') {
        if (!isWidgetAvailable(key)) {
          return (
            <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-bold flex items-center gap-2">
                    <FileText size={18} /> Indicadores de credenciamento
                  </h3>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Servico COORDENACAO inativo para este produto.
                  </p>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold flex items-center gap-2">
                  <FileText size={18} /> Indicadores de credenciamento
                </h3>
                  {pipelineSnapshotError ? (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                      {pipelineSnapshotError}
                    </p>
                  ) : pipelineSnapshotLoading ? (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Carregando indicadores...
                    </p>
                  ) : pipelineSnapshotDate ? (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Snapshot: {pipelineSnapshotDate}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openManualSnapshot}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                      darkMode
                        ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                    title="Snapshot manual"
                    aria-label="Snapshot manual"
                  >
                    <Camera size={12} />
                    Snapshot
                  </button>
                  <div className={`${darkMode ? 'bg-vision-surface text-gray-100 border border-vision-border' : 'bg-gray-100 text-gray-700 border border-gray-200'} px-3 py-1 rounded-full text-xs font-semibold`}>
                    Base: {pipelineSnapshot ? Number(pipelineSnapshot.total || 0) : '--'}
                  </div>
                </div>
              </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {generalKpis.length === 0 && !pipelineSnapshotLoading ? (
                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Indicadores indisponíveis.
                </div>
              ) : null}
              {generalKpis.map((kpi) => {
                const toneClass =
                  kpi.delta.tone === "up"
                    ? (darkMode ? "text-green-300 bg-green-900/30" : "text-green-800 bg-green-100")
                    : kpi.delta.tone === "down"
                    ? (darkMode ? "text-red-300 bg-red-900/30" : "text-red-700 bg-red-100")
                    : (darkMode ? "text-gray-300 bg-vision-surface" : "text-gray-600 bg-gray-100");

                return (
                  <div
                    key={kpi.id}
                    className={`rounded-lg border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {kpi.label}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${toneClass}`}>
                        {kpi.delta.label}
                      </span>
                    </div>
                    <div className="mt-2 flex items-end gap-2">
                      <span className="text-2xl font-bold">{kpi.value}</span>
                      {kpi.unit ? <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{kpi.unit}</span> : null}
                    </div>
                    {kpi.helper ? (
                      <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{kpi.helper}</p>
                    ) : null}
                    {kpi.progress !== undefined ? (
                      <div className={`mt-3 h-1.5 rounded-full ${darkMode ? 'bg-vision-darkGreen' : 'bg-gray-200'}`}>
                        <div
                          className="h-1.5 rounded-full"
                          style={{ width: `${kpi.progress}%`, backgroundColor: kpi.progressColor }}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      if (key === 'attendance_kpis') {
        if (!isWidgetAvailable(key)) {
          return (
            <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-bold flex items-center gap-2">
                    <MessageSquare size={18} /> Atendimentos
                  </h3>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Servico COORDENACAO inativo para este produto.
                  </p>
                </div>
              </div>
            </div>
          );
        }
        const rangeLabel = attendanceKpis
          ? `${attendanceKpis.range.from} a ${attendanceKpis.range.to}`
          : 'Ultimos 7 dias';
        const respondedCount = attendanceKpis?.firstResponse.respondedCount ?? 0;
        const withinSlaCount = attendanceKpis?.firstResponse.withinSlaCount ?? 0;
        const closedTotal = attendanceKpis?.closed.total ?? 0;
        const discardTotal = attendanceKpis?.discard.total ?? 0;
        const discardBase = closedTotal + discardTotal;

        const items = [
          {
            id: 'tfr',
            label: '1a resposta',
            value: formatDurationShort(attendanceKpis?.firstResponse.avgSeconds),
            helper: 'media',
          },
          {
            id: 'sla',
            label: 'SLA 1h',
            value: formatPercent(attendanceKpis?.firstResponse.withinSlaPct),
            helper: respondedCount ? `${withinSlaCount}/${respondedCount}` : 'sem base',
          },
          {
            id: 'resolucao',
            label: 'Tempo de resolucao',
            value: formatDurationShort(attendanceKpis?.resolution.avgSeconds),
            helper: 'media',
          },
          {
            id: 'backlog',
            label: 'Backlog aberto',
            value: attendanceKpis ? String(attendanceKpis.backlog.total) : '--',
          },
          {
            id: 'encerrados',
            label: 'Encerrados (7d)',
            value: attendanceKpis ? String(attendanceKpis.closed.total) : '--',
          },
          {
            id: 'descarte',
            label: 'Taxa de descarte',
            value: formatPercent(attendanceKpis?.discard.rate),
            helper: discardBase ? `${discardTotal}/${discardBase}` : 'sem base',
          },
        ];

        return (
          <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold flex items-center gap-2">
                  <MessageSquare size={18} /> Atendimentos
                </h3>
                {attendanceKpisError ? (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                    {attendanceKpisError}
                  </p>
                ) : attendanceKpisLoading ? (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Carregando KPIs...
                  </p>
                ) : (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Periodo: {rangeLabel}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`rounded-lg border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}
                >
                  <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {item.label}
                  </p>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="text-2xl font-bold">{item.value}</span>
                  </div>
                  {item.helper ? (
                    <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.helper}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );
      }

      if (key === 'engagement_kpis') {
        const snapshot = engagementSnapshot?.engajamento;
        const totalAgencies = snapshot?.imobiliariasTotal ?? 0;
        const totalTickets = snapshot?.chamadosTotal ?? 0;
        const avgTickets = snapshot
          ? String(snapshot.mediaChamadosPorImobiliaria ?? 0).replace('.', ',')
          : '0,0';
        const topCategory = snapshot?.categoriaMaisFrequente?.nome || 'Sem dados';

        const stats = [
          { id: 'agencies', label: 'Imobiliarias', value: String(totalAgencies), helper: 'Total' },
          { id: 'tickets', label: 'Chamados', value: String(totalTickets), helper: 'Total' },
          { id: 'avg', label: 'Media por imobiliaria', value: avgTickets, helper: 'Chamados' },
          { id: 'top', label: 'Top categoria', value: topCategory, helper: 'Ranking' },
        ];

        return (
          <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold flex items-center gap-2">
                  <Trophy size={18} /> Engajamento
                </h3>
                {engagementError ? (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                    {engagementError}
                  </p>
                ) : engagementLoading ? (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Carregando engajamento...
                  </p>
                ) : (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Periodo: {engagementSnapshot?.range?.from || '--'} a {engagementSnapshot?.range?.to || '--'}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {stats.map((item) => (
                <div
                  key={item.id}
                  className={`rounded-lg border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}
                >
                  <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {item.label}
                  </p>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="text-2xl font-bold">{item.value}</span>
                  </div>
                  {item.helper ? (
                    <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.helper}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );
      }

      return null;
    };

    return (
      <div className={`p-6 md:p-8 min-h-full animate-in fade-in slide-in-from-right-4 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
      {changeCategoryModal}
      {bulkActionModal}
        
        {/* Navigation */}
        <button 
          onClick={handleBackToList}
          className={`flex items-center gap-2 text-sm font-semibold hover:underline mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
        >
          <ArrowLeft size={16} /> Voltar para lista de produtos
        </button>

        {/* Header Hero */}
        <div className={`
           mb-8 p-6 rounded-2xl border flex flex-col md:flex-row justify-between items-start md:items-center gap-6 shadow-sm
           ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
         `}>
           <div className="flex items-center gap-4">
              <div className={`
                 w-16 h-16 rounded-xl flex items-center justify-center shadow-inner
                 ${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-green-100 text-[#294b29]'}
              `}>
                 <Package size={32} />
              </div>
              <div>
                 <h1 className="text-2xl font-bold leading-tight">{selectedProduct.name}</h1>
                 <p className={`text-sm mt-1 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Building size={14} />
                    {selectedProduct.builder || 'Cidade/Estado indefinidos'} | {(selectedProduct as any).praca || (selectedProduct as any).pracaNome || 'Praça não vinculada'}</p>
                      </div>
           </div>
        </div>

        {/* Abas de serviços */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className={`flex flex-wrap gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {availableTabs.map((tab) => {
              const isActive = serviceTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleServiceTabChange(tab.key as any)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    isActive
                      ? darkMode
                        ? 'bg-vision-activeRed border-vision-activeRed text-white'
                        : 'bg-[#294b29] border-[#294b29] text-white'
                      : darkMode
                      ? 'border-vision-border hover:bg-vision-surface'
                      : 'border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            {serviceTab === 'GERAL' && (
              <button
                type="button"
                onClick={openDateControls}
                className={`flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] transition-colors ${
                  darkMode
                    ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
                title="Definir periodo do snapshot"
                aria-label="Definir periodo do snapshot"
              >
                <Calendar size={14} />
                <span className="font-semibold">Periodo</span>
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentPresetLabel} - {widgetDateFrom} a {widgetDateTo}
                </span>
              </button>
            )}
            {serviceTab === 'GERAL' && (
              <button
                type="button"
                onClick={toggleWidgetSettings}
                className={`p-2 rounded-lg border transition-colors ${
                  darkMode
                    ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
                title="Configurar widgets"
                aria-label="Configurar widgets"
              >
                <Settings size={16} />
              </button>
            )}
            {serviceTab === 'Coordenacao' && selectedProduct?.id && (
              <>
                <button
                  type="button"
                  onClick={handleRefreshCoordination}
                  disabled={isRefreshingCoordination}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors flex items-center gap-2 ${
                    darkMode
                      ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                  } ${isRefreshingCoordination ? 'opacity-70 cursor-not-allowed' : ''}`}
                  title="Atualizar dados de coordenação"
                >
                  <RefreshCw size={12} className={isRefreshingCoordination ? 'animate-spin' : ''} />
                  Atualizar
                </button>
                <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Atualizado: {coordinationUpdatedAt ? coordinationUpdatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                </span>
              </>
            )}
          </div>
        </div>


        {serviceTab === 'GERAL' && renderWidgetDateControls()}

          {serviceTab === 'GERAL' && renderWidgetSettingsModal()}


          {serviceTab === 'GERAL' && renderManualSnapshotModal()}

        {/* Dashboard Grid */}
        {serviceTab === 'GERAL' && (
        <div className="grid grid-cols-1 gap-8">
           <div className="flex flex-col gap-6">
              {isLoadingDetail ? (
                <div className={`p-6 rounded-xl border shadow-sm flex items-center justify-center gap-2 text-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-300' : 'bg-white border-gray-200 text-gray-600'}`}>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Carregando snapshots...
                </div>
              ) : (
                generalWidgetOrder.map((key) => renderGeneralWidget(key))
              )}

              {false && (
                <div className="grid md:grid-cols-4 gap-3">
                  <div className={`${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'} rounded-lg p-3 border`}>
                    <div className="flex items-center justify-between text-xs opacity-70">
                      <span>Conversão pipeline</span>
                      <div className="flex gap-1">
                        {[{ key: '4w', label: '4s' }, { key: '8w', label: '8s' }].map(opt => (
                          <button
                            key={opt.key}
                            onClick={() => setCoordinationRange(opt.key as '4w' | '8w')}
                            className={`px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors ${
                              coordinationRange === opt.key
                                ? (darkMode ? 'bg-vision-activeRed border-vision-activeRed text-white' : 'bg-[#294b29] border-[#294b29] text-white')
                                : (darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-200 text-gray-700 hover:bg-gray-100')
                            }`}
                            title={opt.key === '4w' ? 'Últimas 4 semanas' : 'Últimas 8 semanas'}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-xl font-bold mt-2">{coordinationKpis.conversion}%</p>
                      </div>
                  <div className={`${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'} rounded-lg p-3 border`}>
                    <p className="text-xs opacity-70">Backlog (análise + credenciando)</p>
                    <p className="text-xl font-bold mt-2">{coordinationKpis.backlog}</p>
                      </div>
                  <div className={`${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'} rounded-lg p-3 border`}>
                    <p className="text-xs opacity-70">Ativos no periodo</p>
                    <p className="text-xl font-bold mt-2">{coordinationStatusTotals.active}</p>
                      </div>
                  <div className={`${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'} rounded-lg p-3 border`}>
                    <p className="text-xs opacity-70">Tempo medio ate ativar</p>
                    <p className="text-xl font-bold mt-2">{coordinationKpis.avgActivationDays} dias</p>
                      </div>
                </div>
              )}
            </div>
        </div>
        )}

        {serviceTab === 'Coordenacao' && (
        <div className="flex flex-col gap-4">
          <div className={`rounded-xl border p-2 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'credenciamento', label: 'Credenciamento' },
                { key: 'atendimentos', label: 'Atendimentos' },
              ].map((tab) => {
                const isActive = coordinationTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setCoordinationTab(tab.key as 'credenciamento' | 'atendimentos')}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      isActive
                        ? darkMode
                          ? 'bg-vision-activeRed text-white'
                          : 'bg-[#294b29] text-white'
                        : darkMode
                        ? 'text-gray-200 hover:bg-vision-surface'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {coordinationTab === 'credenciamento' && (
              <>
                <div className={`rounded-xl border shadow-sm p-3 flex flex-col gap-3 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                      <h4 className="text-sm font-bold opacity-70 flex items-center gap-2">
                          <Filter size={16} /> Funil de Parceria
                      </h4>
                      {isLoadingTickets && (
                        <div className="flex items-center gap-2 text-[10px] opacity-70">
                          <Loader2 className="w-3 h-3 animate-spin" /> Atualizando funil...
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => registrationEnabled && registrationLink && window.open(registrationLink, '_blank')}
                          disabled={!registrationEnabled || !registrationLink}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors flex items-center gap-2 ${
                            registrationEnabled
                              ? (darkMode ? 'border-vision-border text-gray-100 hover:bg-vision-surface' : 'border-gray-200 text-gray-700 hover:bg-gray-100')
                              : 'opacity-50 cursor-not-allowed'
                          }`}
                        >
                          <ExternalLink size={12} />
                          Formulario
                        </button>
                        <button
                          onClick={() => handleToggleRegistration(selectedProduct.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                            registrationEnabled
                              ? (darkMode ? 'border-red-900/40 text-red-300 hover:bg-red-900/20' : 'border-red-200 text-red-700 hover:bg-red-50')
                              : (darkMode ? 'border-green-900/40 text-green-300 hover:bg-green-900/20' : 'border-green-200 text-green-800 hover:bg-green-50')
                        }`}
                        >
                          {registrationEnabled ? 'Fechar credenciamento' : 'Abrir credenciamento'}
                        </button>
                      </div>
                  </div>
                      <div className="grid grid-cols-5 gap-2">
                          <button
                            type="button"
                            onClick={() => setStatusFilters([])}
                            className={`p-3 rounded-lg text-center border transition-all active:scale-95 ${
                              statusFilters.length === 0
                                ? (darkMode ? 'bg-vision-activeRed text-white border-vision-activeRed shadow-md ring-2 ring-vision-activeRed/30' : 'bg-[#294b29] text-white border-[#294b29] shadow-md ring-2 ring-[#294b29]/30')
                                : (darkMode ? 'bg-vision-surface/70 border-vision-border text-gray-200 hover:bg-vision-surface' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100')
                            }`}
                            aria-label="Limpar filtros de status"
                          >
                              <span className={`block text-xl font-bold ${
                                statusFilters.length === 0
                                  ? 'text-white'
                                  : (darkMode ? 'text-white' : 'text-gray-900')
                              }`}>
                                {agencyCounts.analysis + agencyCounts.credentialing + agencyCounts.active + agencyCounts.blocked}
                              </span>
                              <span className={`text-[10px] font-bold uppercase ${
                                statusFilters.length === 0
                                  ? 'text-white/90'
                                  : (darkMode ? 'text-gray-300' : 'text-gray-600')
                              }`}>Total</span>
                          </button>
                          <button
                            onClick={() => handleToggleFilter('analysis')}
                            className={`p-3 rounded-lg text-center border transition-all active:scale-95 ${isStatusSelected('analysis') ? 'bg-blue-600 text-white border-blue-700 shadow-md ring-2 ring-blue-500/30' : (darkMode ? 'bg-blue-900/10 border-blue-900/30 hover:bg-blue-900/20' : 'bg-blue-50 border-blue-100 hover:bg-blue-100')}`}
                          >
                              <span className={`block text-xl font-bold ${isStatusSelected('analysis') ? 'text-white' : (darkMode ? 'text-blue-400' : 'text-blue-700')}`}>{agencyCounts.analysis}</span>
                              <span className={`text-[10px] font-bold uppercase ${isStatusSelected('analysis') ? 'text-white/90' : (darkMode ? 'text-blue-500' : 'text-blue-600')}`}>Análise</span>
                          </button>

                          <button
                            onClick={() => handleToggleFilter('credentialing')}
                            className={`p-3 rounded-lg text-center border transition-all active:scale-95 ${isStatusSelected('credentialing') ? 'bg-yellow-500 text-white border-yellow-600 shadow-md ring-2 ring-yellow-500/30' : (darkMode ? 'bg-yellow-900/10 border-yellow-900/30 hover:bg-yellow-900/20' : 'bg-yellow-50 border-yellow-100 hover:bg-yellow-100')}`}
                          >
                              <span className={`block text-xl font-bold ${isStatusSelected('credentialing') ? 'text-white' : (darkMode ? 'text-yellow-400' : 'text-yellow-700')}`}>{agencyCounts.credentialing}</span>
                              <span className={`text-[10px] font-bold uppercase ${isStatusSelected('credentialing') ? 'text-white/90' : (darkMode ? 'text-yellow-500' : 'text-yellow-600')}`}>Credenciando</span>
                          </button>

                          <button
                            onClick={() => handleToggleFilter('active')}
                            className={`p-3 rounded-lg text-center border transition-all active:scale-95 ${isStatusSelected('active') ? 'bg-green-700 text-white border-green-800 shadow-md ring-2 ring-green-600/30' : (darkMode ? 'bg-green-900/10 border-green-900/30 hover:bg-green-900/20' : 'bg-green-50 border-green-100 hover:bg-green-100')}`}
                          >
                              <span className={`block text-xl font-bold ${isStatusSelected('active') ? 'text-white' : (darkMode ? 'text-green-600' : 'text-green-800')}`}>{agencyCounts.active}</span>
                              <span className={`text-[10px] font-bold uppercase ${isStatusSelected('active') ? 'text-white/90' : (darkMode ? 'text-green-600' : 'text-green-600')}`}>Ativas</span>
                          </button>

                          <button
                            onClick={() => handleToggleFilter('blocked')}
                            className={`p-3 rounded-lg text-center border transition-all active:scale-95 ${isStatusSelected('blocked') ? 'bg-red-600 text-white border-red-700 shadow-md ring-2 ring-red-500/30' : (darkMode ? 'bg-red-900/10 border-red-900/30 hover:bg-red-900/20' : 'bg-red-50 border-red-100 hover:bg-red-100')}`}
                          >
                              <span className={`block text-xl font-bold ${isStatusSelected('blocked') ? 'text-white' : (darkMode ? 'text-red-400' : 'text-red-700')}`}>{agencyCounts.blocked}</span>
                              <span className={`text-[10px] font-bold uppercase ${isStatusSelected('blocked') ? 'text-white/90' : (darkMode ? 'text-red-500' : 'text-red-600')}`}>Suspensas</span>
                          </button>
                      </div>
                      {statusFilters.length > 0 ? (
                        <div className={`rounded-lg border p-3 flex flex-col gap-2 ${darkMode ? 'bg-vision-surface/70 border-vision-border' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase opacity-70">
                              <Tag size={14} />
                              Tags
                              {selectedAgencyTags.length > 0 && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                  {tagFilteredCount}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className={`flex rounded-full border overflow-hidden text-[10px] font-semibold ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
                                <button
                                  type="button"
                                  onClick={() => setAgencyTagsMode('any')}
                                  className={`px-3 py-1 ${agencyTagsMode === 'any' ? (darkMode ? 'bg-vision-activeRed text-white' : 'bg-[#294b29] text-white') : (darkMode ? 'text-gray-300 hover:bg-vision-surface' : 'text-gray-600 hover:bg-gray-100')}`}
                                >
                                  Qualquer
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setAgencyTagsMode('all')}
                                  className={`px-3 py-1 ${agencyTagsMode === 'all' ? (darkMode ? 'bg-vision-activeRed text-white' : 'bg-[#294b29] text-white') : (darkMode ? 'text-gray-300 hover:bg-vision-surface' : 'text-gray-600 hover:bg-gray-100')}`}
                                >
                                  Todas
                                </button>
                              </div>
                              {selectedAgencyTags.length > 0 && (
                                <button
                                  type="button"
                                  onClick={clearAgencyTags}
                                  className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                                >
                                  Limpar
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {availableAgencyTags.length > 0 ? (
                              availableAgencyTags.map((tag) => {
                                const active = selectedAgencyTags.includes(tag.name);
                                return (
                                  <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => toggleAgencyTag(tag.name)}
                                    className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                                      active
                                        ? (darkMode ? 'bg-white text-gray-900 border-white' : 'bg-[#294b29] text-white border-[#294b29]')
                                        : (darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100')
                                    }`}
                                  >
                                    {tag.name}
                                  </button>
                                );
                              })
                            ) : (
                              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhuma tag cadastrada.</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={`rounded-lg border px-3 py-2 text-xs ${darkMode ? 'bg-vision-surface/70 border-vision-border text-gray-500' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                          Selecione um status para filtrar por tags.
                        </div>
                      )}
                  </div>
                <div className={`rounded-xl border shadow-sm overflow-hidden flex flex-col min-h-[400px] ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                 <div className={`p-3 border-b flex flex-col md:flex-row justify-between items-start md:items-center gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
                    <div>
                      <h3 className="font-bold text-lg flex items-center gap-2">
                         <Building2 size={20} className={darkMode ? 'text-white' : 'text-[#294b29]'} />
                         Imobiliárias Associadas
                      </h3>
                      {isAdmin ? (
                        <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={bulkSelectionEnabled}
                              onChange={handleBulkSelectionToggle}
                              aria-label="Ativar selecao em massa"
                              className={`h-4 w-4 rounded border transition-colors ${
                                darkMode
                                  ? 'bg-vision-surface border-vision-border text-white accent-white'
                                  : 'bg-white border-gray-300 text-[#294b29] accent-[#294b29]'
                              }`}
                            />
                            <span>{activeFilterLabel ? activeFilterLabel : `Total de Imobiliarias: ${agenciesTotalCount}`}</span>
                          </label>
                        </div>
                      ) : (
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {activeFilterLabel ? activeFilterLabel : `Total de Imobiliarias: ${agenciesTotalCount}`}
                        </p>
                      )}
                      {isAdmin && bulkSelectionEnabled && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setBulkActionOpen(true)}
                            disabled={!selectedAgencyIds.length}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors inline-flex items-center gap-2 ${
                              selectedAgencyIds.length
                                ? darkMode
                                  ? 'border-vision-border text-gray-100 hover:bg-vision-surface'
                                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                                : 'opacity-50 cursor-not-allowed'
                            }`}
                          >
                            <Settings size={12} />
                            Acao em massa
                          </button>
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {selectedAgencyIds.length ? `${selectedAgencyIds.length} selecionadas` : 'Selecione as imobiliarias'}
                          </span>
                        </div>
                      )}
                      </div>

                    <div className="relative w-full md:w-64">
                       <Search className={`absolute left-3 top-2.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={16} />
                       <input 
                          type="text" 
                          placeholder="Buscar imobiliárias..."
                          value={agencySearch}
                          onChange={(e) => setAgencySearch(e.target.value)}
                          className={`
                            w-full pl-9 pr-4 py-2 rounded-lg border outline-none text-sm transition-all
                            ${darkMode 
                              ? 'bg-vision-surface border-vision-border text-white focus:ring-1 focus:ring-gray-600' 
                              : 'bg-gray-50 border-gray-300 text-gray-800 focus:ring-1 focus:ring-[#294b29]'
                            }
                          `}
                       />
                    </div>
                 </div>
                 
                 <div className="overflow-x-auto">
                    <table className="w-full text-left">
                       <thead className={`${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-gray-50 text-gray-600'} text-xs uppercase`}>
                          <tr>
                             {bulkSelectionEnabled && (
                               <th className="px-4 py-4 font-semibold"></th>
                             )}
                             <th className="px-6 py-4 font-semibold">
                               <button
                                 type="button"
                                 onClick={() => handleAgencySort('name')}
                                 className="inline-flex items-center gap-1 hover:underline decoration-dotted underline-offset-4"
                               >
                                 Imobiliárias{getAgencySortIndicator('name')}
                               </button>
                             </th>
                             <th className="px-6 py-4 font-semibold text-center">Vendas</th>
                             <th className="px-6 py-4 font-semibold text-right">VGV</th>
                             <th className="px-6 py-4 font-semibold text-center">
                               <button
                                 type="button"
                                 onClick={() => handleAgencySort('status')}
                                 className="inline-flex items-center gap-1 hover:underline decoration-dotted underline-offset-4"
                               >
                                 Status{getAgencySortIndicator('status')}
                               </button>
                             </th>
                          </tr>
                       </thead>
                       <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-100'}`}>
                          {pagedAssociatedAgencies.length > 0 ? (
                            pagedAssociatedAgencies.map((agency) => (
                             <tr 
                               key={agency.id} 
                               className={`${darkMode ? 'text-gray-300 hover:bg-vision-surface' : 'text-gray-700 hover:bg-gray-50'} ${bulkSelectionEnabled ? 'cursor-default' : 'cursor-pointer'}`}
                               onClick={() => {
                                 if (!bulkSelectionEnabled) {
                                   const base = typeof window !== 'undefined' ? window.location.origin : '';
                                   const target = `${base}/imobiliarias/${agency.id}/visao-geral`;
                                   window.open(target, '_blank', 'noopener,noreferrer');
                                 }
                               }}
                             >
                                {bulkSelectionEnabled && (
                                  <td className="px-4 py-4">
                                    <input
                                      type="checkbox"
                                      checked={selectedAgencyIds.includes(agency.id)}
                                      onChange={() => toggleAgencySelection(agency.id)}
                                      onClick={(event) => event.stopPropagation()}
                                      aria-label={`Selecionar imobiliaria ${agency.name}`}
                                      className={`h-4 w-4 rounded border transition-colors ${
                                        darkMode
                                          ? 'bg-vision-surface border-vision-border text-white accent-white'
                                          : 'bg-white border-gray-300 text-[#294b29] accent-[#294b29]'
                                      }`}
                                    />
                                  </td>
                                )}
                                <td className="px-6 py-4 font-medium">
                                   {agency.name}
                                </td>
                                <td className="px-6 py-4 text-center text-sm">
                                   <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{agency.sold}</span>
                                </td>
                                <td className="px-6 py-4 text-right font-mono text-sm">
                                   {agency.vgv}
                                </td>
                                <td className="px-6 py-4 text-center">
                                   <StatusBadge status={agency.status} />
                                </td>
                             </tr>
                            ))
                          ) : (
                             <tr>
                                <td colSpan={4} className="p-8 text-center text-gray-500">
                                   Nenhuma imobiliária encontrada {(statusFilters.length > 0 || selectedAgencyTags.length > 0) && 'neste filtro'}.
                                </td>
                             </tr>
                          )}
                       </tbody>
                    </table>
                    <div className="px-6 py-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] border-t border-gray-100 dark:border-vision-border">
                      <div className="flex items-center gap-2">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Itens por pagina</span>
                        <select
                          value={
                            agenciesPageSize === 'all'
                              ? 'all'
                              : [5, 10, 30, 50].includes(agenciesPageSize)
                              ? String(agenciesPageSize)
                              : 'custom'
                          }
                          onChange={(event) => {
                            const value = event.target.value;
                            if (value === 'custom') {
                              setCustomAgenciesPageSizeActive(true);
                              setCustomAgenciesPageSize(
                                agenciesPageSize === 'all' ? '' : String(agenciesPageSize)
                              );
                              return;
                            }
                            setCustomAgenciesPageSizeActive(false);
                            setAgenciesPageSize(value === 'all' ? 'all' : Number(value));
                          }}
                          className={`px-2 py-1 rounded border text-[11px] ${
                            darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-700'
                          }`}
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="30">30</option>
                          <option value="50">50</option>
                          <option value="all">Todas</option>
                          <option value="custom">Personalizado</option>
                        </select>
                        {customAgenciesPageSizeActive && (
                          <input
                            type="number"
                            min={1}
                            placeholder="Qtd"
                            value={customAgenciesPageSize}
                            onChange={(event) => setCustomAgenciesPageSize(event.target.value)}
                            onBlur={() => {
                              const value = Number(customAgenciesPageSize);
                              if (Number.isFinite(value) && value > 0) {
                                setAgenciesPageSize(Math.min(value, 5000));
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                const value = Number(customAgenciesPageSize);
                                if (Number.isFinite(value) && value > 0) {
                                  setAgenciesPageSize(Math.min(value, 5000));
                                }
                              }
                            }}
                            className={`w-20 px-2 py-1 rounded border text-[11px] ${
                              darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-700'
                            }`}
                          />
                        )}
                      </div>
                      {agenciesPages > 1 && (
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setAgenciesPage((prev) => Math.max(1, prev - 1))}
                            disabled={agenciesPage <= 1}
                            className={`px-2 py-1 rounded border ${
                              agenciesPage <= 1
                                ? 'opacity-50 cursor-not-allowed'
                                : darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Anterior
                          </button>
                          <span>
                            Pagina {agenciesPage} de {agenciesPages}
                          </span>
                          <button
                            type="button"
                            onClick={() => setAgenciesPage((prev) => Math.min(agenciesPages, prev + 1))}
                            disabled={agenciesPage >= agenciesPages}
                            className={`px-2 py-1 rounded border ${
                              agenciesPage >= agenciesPages
                                ? 'opacity-50 cursor-not-allowed'
                                : darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Proxima
                          </button>
                        </div>
                      )}
                    </div>
                 </div>
               </div>
              </>
            )}
            {coordinationTab === 'atendimentos' && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={`p-3 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <MessageSquare size={18} /> Chamados frequentes
                  </h3>
                  {ticketRankingLoading && (
                    <div className="flex items-center gap-3 text-sm opacity-70">
                      <Loader2 className="w-4 h-4 animate-spin" /> Carregando ranking...
                    </div>
                  )}
                  {!ticketRankingLoading && ticketRankingError && (
                    <p className="text-sm text-yellow-500">{ticketRankingError}</p>
                  )}
                  {!ticketRankingLoading && !ticketRankingError && ticketRankingSummary.highest === 0 && (
                    <p className="text-sm opacity-60">Nenhum chamado registrado ainda.</p>
                  )}
                  {!ticketRankingLoading && !ticketRankingError && ticketRankingSummary.highest !== 0 && (
                    <div className="space-y-3">
                      {ticketRankingSummary.ordered.map((item) => {
                        const isActive =
                          ticketCategoryFilter?.id === (item.categoryId ?? null) &&
                          ticketCategoryFilter?.label === item.category;
                        return (
                          <button
                            key={`${item.category}-${item.categoryId != null ? item.categoryId : 'null'}`}
                            type="button"
                            onClick={() =>
                              setTicketCategoryFilter((prev) => {
                                if (prev && prev.id === (item.categoryId ?? null) && prev.label === item.category) {
                                  return null;
                                }
                                return { id: item.categoryId ?? null, label: item.category };
                              })
                            }
                            className={`w-full text-left space-y-1 rounded-lg px-2 py-2 transition-colors ${
                              isActive
                                ? darkMode
                                  ? 'bg-vision-surface/80'
                                  : 'bg-gray-100'
                                : darkMode
                                ? 'hover:bg-vision-surface/50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">{item.category}</span>
                              <span className="text-xs opacity-70">{item.count} ocorrências</span>
                            </div>
                            <div className={`h-2 rounded-full ${darkMode ? 'bg-vision-surface' : 'bg-gray-200'}`}>
                              <div
                                className={`${darkMode ? 'bg-vision-activeRed' : 'bg-[#294b29]'} h-2 rounded-full transition-all`}
                                style={{ width: ticketRankingSummary.highest ? ((item.count / ticketRankingSummary.highest) * 100) + '%' : '0%' }}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                   )}
                </div>
                <div className={`p-3 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                   <div className="flex items-start justify-between gap-4 mb-4">
                     <h3 className="font-bold flex items-center gap-2">
                       <MessageSquare size={18} /> Últimos atendimentos
                     </h3>
                     {ticketCategoryFilter && (
                       <button
                         type="button"
                         onClick={() => setTicketCategoryFilter(null)}
                         className={`text-[11px] font-semibold px-2 py-1 rounded border ${
                           darkMode
                             ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                             : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                         }`}
                         title="Limpar filtro"
                       >
                         {ticketCategoryFilter.label} •
                       </button>
                     )}
                   </div>
                   
                   {isLoadingTickets ? (
                     <div className="flex items-center gap-3 text-sm opacity-70">
                       <Loader2 className="w-4 h-4 animate-spin" /> Carregando atendimentos...
                     </div>
                   ) : ticketError ? (
                     <p className="text-sm text-yellow-500">{ticketError}</p>
                   ) : filteredTickets.length === 0 ? (
                     <p className="text-sm opacity-60">Nenhum atendimento para este produto.</p>
                   ) : (
                   <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                         <thead>
                            <tr className={`opacity-60 border-b ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
                               <th className="pb-2 font-semibold">Atendimento</th>
                               <th className="pb-2 font-semibold">Contato</th>
                               <th className="pb-2 font-semibold text-right">Categoria</th>
                            </tr>
                         </thead>
                         <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-100'}`}>
                              {pagedTickets.map((ticket) => (
                                <tr 
                                  key={ticket.id} 
                                  className={`${darkMode ? 'text-gray-300 hover:bg-vision-surface' : 'text-gray-700 hover:bg-gray-50'} cursor-pointer`}
                                  onClick={() => fetchTicketMessages(ticket)}
                                >
                                   <td className="py-2 align-top">
                                      <div className="flex flex-col">
                                         <span className="font-mono opacity-80">#{ticket.id}</span>
                                         <span className="text-[10px] opacity-60">{formatTicketDate(ticket.updated_at || ticket.created_at)}</span>
                                         {ticket.status && !['encerrado', 'descartado'].includes(ticket.status) && (
                                           <span
                                             className={`mt-1 inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                               darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                                             }`}
                                           >
                                             Em andamento
                                           </span>
                                         )}
                                      </div>
                                   </td>
                                   <td className="py-2 align-top">
                                      <div className="flex flex-col">
                                         <span className="font-medium">{ticket.contato_nome || 'Sem nome'}</span>
                                         <span className="opacity-70 text-[10px] truncate max-w-[160px]" title={ticket.contato_whatsapp}>
                                           {formatPhone(ticket.contato_whatsapp)}
                                         </span>
                                      </div>
                                   </td>
                                   <td className="py-2 text-right align-top">
                                      {(() => {
                                        const cat = coordinationCategories.find(c => c.id === (ticket.categoria_id || -1));
                                        const label = cat?.nome || ticket.categoria_nome || 'Indefinido';
                                        const bg = cat?.cor || null;
                                        return (
                                          <span
                                            className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${bg ? 'text-white' : 'bg-gray-100 dark:bg-vision-surface text-gray-700 dark:text-gray-200'}`}
                                            style={bg ? { backgroundColor: bg } : undefined}
                                          >
                                            {label}
                                          </span>
                                        );
                                      })()}
                                   </td>
                                </tr>
                              ))}
                          </tbody>
                       </table>
                       <p className="text-[10px] mt-2 text-gray-500">Clique em um atendimento para ver a conversa.</p>
                       {ticketPages > 1 && (
                         <div className="mt-3 flex items-center justify-between text-[11px]">
                           <button
                             type="button"
                             onClick={() => setTicketsPage((prev) => Math.max(1, prev - 1))}
                             disabled={ticketsPage <= 1}
                             className={`px-2 py-1 rounded border ${
                               ticketsPage <= 1
                                 ? 'opacity-50 cursor-not-allowed'
                                 : darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                             }`}
                           >
                             Anterior
                           </button>
                           <span>
                             Pagina {ticketsPage} de {ticketPages}
                           </span>
                           <button
                             type="button"
                             onClick={() => setTicketsPage((prev) => Math.min(ticketPages, prev + 1))}
                             disabled={ticketsPage >= ticketPages}
                             className={`px-2 py-1 rounded border ${
                               ticketsPage >= ticketPages
                                 ? 'opacity-50 cursor-not-allowed'
                                 : darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                             }`}
                           >
                             Proxima
                           </button>
                         </div>
                       )}
                    </div>
                   )}
                </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {serviceTab !== 'GERAL' && serviceTab !== 'Coordenacao' && (serviceTab === 'PESQUISA' ? (
          <div className={`p-6 rounded-2xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">Pesquisa de mercado</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Estrutura de pastas para armazenar e baixar arquivos de pesquisa.</p>
                      </div>
              <button
                className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-100'}`}
              >
                Enviar arquivo
              </button>
            </div>
            <div className="space-y-2">
              {renderPesquisaNodes(pesquisaTree)}
            </div>
          </div>
        ) : (
          <div className={`p-8 rounded-2xl border text-center ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-300' : 'bg-white border-gray-200 text-gray-600'}`}>
            Conteúdo do serviço "{serviceTab}" em breve.
          </div>
        ))}

                {selectedTicket && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onKeyDown={(e) => { if (e.key === 'Escape') { setSelectedTicket(null); setTicketMessages([]); } }} tabIndex={-1}>
            <div className={`${darkMode ? 'bg-vision-darkGreen text-white' : 'bg-white text-gray-900'} w-full max-w-3xl rounded-2xl shadow-xl border ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between p-4 border-b">
                <div className="space-y-1">
                  <h3 className="text-xl font-bold">{selectedTicket.contato_nome || 'Sem nome'}</h3>
                  <p className="text-sm opacity-70 flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs">#{selectedTicket.id}</span>
                    <span className="text-xs">{formatPhone(selectedTicket.contato_whatsapp)}</span>
                    {(() => {
                      const cat = coordinationCategories.find(c => c.id === (selectedTicket.categoria_id || -1));
                      const label = cat?.nome || selectedTicket.categoria_nome || 'Indefinido';
                      const bg = cat?.cor || null;
                      return (
                        <button
                          type="button"
                          className={`px-2 py-0.5 rounded text-[11px] ${bg ? 'text-white' : 'bg-gray-200 dark:bg-vision-surface text-gray-700 dark:text-gray-200'}`}
                          style={bg ? { backgroundColor: bg } : undefined}
                          onClick={() => {
                            console.log('ProductsPage badge click role:', role);
                            if (isAdmin) {
                              setChangeCategoryModalOpen(true);
                              setChangeCategoryValue(selectedTicket.categoria_id ? String(selectedTicket.categoria_id) : '');
                            }
                          }}
                          disabled={!isAdmin}
                        >
                          {label}
                        </button>
                      );
                    })()}</p>
                      </div>
                <button onClick={() => { setSelectedTicket(null); setTicketMessages([]); }} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-vision-surface">
                  <X size={16} />
                </button>
              </div><div className="max-h-[60vh] overflow-y-auto p-4 space-y-3">
                {isLoadingMessages ? (
                  <div className="flex items-center gap-2 text-sm opacity-70">
                    <Loader2 className="w-4 h-4 animate-spin" /> Carregando conversa...
                  </div>
                ) : ticketMessages.length === 0 ? (
                  <p className="text-sm opacity-70">Sem mensagens registradas.</p>
                ) : (
                  ticketMessages.map((msg) => (
                    msg.direction === 'SYS' ? (
                      <div key={msg.id} className="flex justify-center">
                        <div className={`text-xs px-4 py-1.5 rounded-full shadow-sm ${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-white text-gray-500'}`}>
                          {msg.body}
                        </div>
                      </div>
                    ) : (
                      <div key={msg.id} className={`flex ${msg.direction === 'OUT' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${msg.direction === 'OUT'
                          ? (darkMode ? 'bg-vision-activeRed text-white' : 'bg-[#294b29] text-white')
                          : (darkMode ? 'bg-vision-surface text-gray-100' : 'bg-gray-100 text-gray-800')
                        }`}>
                          {msg.sender_name && <div className="text-[10px] opacity-70 mb-1">{msg.sender_name}</div>}
                          <div className="whitespace-pre-wrap break-words">{msg.body}</div>
                          <div className="text-[10px] opacity-60 mt-1 text-right">{msg.sent_at ? new Date(msg.sent_at).toLocaleString('pt-BR') : ''}</div>
                        </div>
                      </div>
                    )
                  ))
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  return null;
};



















const formatPhone = (value?: string | null) => {
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return "";
  const d = digits.length > 11 ? digits.slice(-11) : digits;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return digits;
};









































































